<script setup name="TvbShakaPlayer">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import shaka from 'shaka-player/dist/shaka-player.ui'
import { generateMixed, isSafari, isSafariBelow17, isTvbInHouseAd } from '@/utils/index'
import 'shaka-player/dist/controls.css'
import { youboras } from '@/utils/gtm'
import { checkAdWhitelist } from '@/api/app'

const emits = defineEmits([
  'stateChange',
  'prev',
  'next',
  'ad-start',
  'ad-end',
  'ad-error',
  'content-resume'
])

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  mute: {
    type: Boolean,
    default: true
  },
  contentId: {
    type: String,
    default: ''
  },
  posterUrl: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  youboraOptions: {
    type: Object,
    default: () => ({})
  },
  playerVars: {
    type: Object,
    default: () => ({})
  },
  /**
   * 控制上一集/下一集按钮的显示与隐藏
   * @type {boolean}
   * @default false - 默认不显示按钮（保持视频详情页的默认行为）
   * @description 通过此参数可以灵活控制按钮的显示状态，适用于不同页面的需求
   */
  showEpisodeNavigation: {
    type: Boolean,
    default: false
  },
  /**
   * 分辨率配置，用于控制分辨率选择功能的显示和切换
   * @type {Object}
   * @default null - 默认不显示分辨率选择
   * @description 格式为 { sd: string, hd: string }，sd和hd分别对应480p和720p的视频URL
   */
  videoClarity: {
    type: Object,
    default: null
  },
  /**
   * 字幕配置，用于控制字幕选择功能的显示和切换
   * @type {Object}
   * @default null - 默认不显示字幕选择
   * @description 格式为 { tc: string, sc: string }，tc和sc分别对应繁體中文和简体中文字幕文件的URL
   */
  subtitle: {
    type: Object,
    default: null
  },
  /**
   * Instream 广告标签 URL (VAST/VMAP)
   */
  adTagUrl: {
    type: String,
    default: ''
  },
  /**
   * 是否显示 Instream 视频广告
   */
  showInstreamAds: {
    type: Boolean,
    default: false
  }
})

const videoId = ref(`shaka-${generateMixed(10)}`)
const videoContainer = ref()
const videoPlayer = ref()
let player = null
let initPromise = null
let ui = null

// localStorage 键名常量
const STORAGE_KEYS = {
  CLARITY: 'tvb_player_clarity_preference',
  SUBTITLE: 'tvb_player_subtitle_preference',
  PLAYBACK_RATE: 'tvb_player_playback_rate_preference',
  MUTE: 'tvb_player_mute_preference',
  VOLUME: 'tvb_player_volume_preference'
}

// 当前设置状态
const currentClarity = ref('hd') // 默认高清
const currentSubtitle = ref('tc') // 默认繁體中文
const currentPlaybackRate = ref(1.0) // 默认正常速度
const currentMuted = ref(true) // 默认静音
const currentVolume = ref(1.0) // 默认最大音量

// 移动端检测状态
const isMobile = ref(false)

// 检测是否支持 Shaka Player UI（Safari 17 以下版本不支持）
const hasShakaUI = () => !isSafariBelow17()

// 检测是否为移动端（s/xs 尺寸）
const checkMobile = () => {
  // 通过 user-agent 判断是否为移动端设备
  const ua = navigator.userAgent.toLowerCase()
  const mobileKeywords = [
    'android',
    'iphone',
    'ipad',
    'ipod',
    'windows phone',
    'mobile',
    'blackberry',
    'webos',
    'opera mini',
    'iemobile'
  ]
  const isMobileUA = mobileKeywords.some((keyword) => ua.includes(keyword))

  // 如果 user-agent 表明是移动端，永远返回 true
  if (isMobileUA) {
    isMobile.value = true
    return true
  }

  // 通过窗口宽度判断是否为移动端
  // s: < 768px, xs: < 480px
  const width = window.innerWidth
  isMobile.value = width < 768
  return isMobile.value
}

// 从 localStorage 读取用户偏好设置
const loadUserPreferences = () => {
  try {
    // 读取分辨率偏好
    const clarityPref = localStorage.getItem(STORAGE_KEYS.CLARITY)
    if (clarityPref && ['sd', 'hd'].includes(clarityPref)) {
      // 根据实际可用的分辨率选项调整默认设置
      const hasSd = props.videoClarity?.sd
      const hasHd = props.videoClarity?.hd

      // 如果只有一个选项可用，忽略用户偏好，使用可用的选项
      if (!hasSd && clarityPref === 'sd') {
        currentClarity.value = 'hd'
      } else if (!hasHd && clarityPref === 'hd') {
        currentClarity.value = 'sd'
      } else if (hasSd || hasHd) {
        currentClarity.value = clarityPref
      }
      // 如果两者都不可用，currentClarity 保持默认值 'hd'
    }

    // 读取字幕偏好
    const subtitlePref = localStorage.getItem(STORAGE_KEYS.SUBTITLE)
    if (subtitlePref && ['tc', 'sc', 'off'].includes(subtitlePref)) {
      const hasTc = props.subtitle?.tc
      const hasSc = props.subtitle?.sc

      // 用户选择关闭字幕时，始终尊重此选择
      if (subtitlePref === 'off') {
        currentSubtitle.value = 'off'
      } else if (!hasTc && subtitlePref === 'tc') {
        // 繁體字幕不可用，切换到简体或关闭
        currentSubtitle.value = hasSc ? 'sc' : 'off'
      } else if (!hasSc && subtitlePref === 'sc') {
        // 简体字幕不可用，切换到繁體或关闭
        currentSubtitle.value = hasTc ? 'tc' : 'off'
      } else if (hasTc || hasSc) {
        // 字幕可用，使用用户偏好
        currentSubtitle.value = subtitlePref
      }
    }

    // 读取播放速度偏好
    const ratePref = localStorage.getItem(STORAGE_KEYS.PLAYBACK_RATE)
    if (ratePref) {
      const rate = parseFloat(ratePref)
      if ([0.5, 1.0, 1.5, 2.0].includes(rate)) {
        currentPlaybackRate.value = rate
      }
    }

    // 读取静音偏好
    const mutePref = localStorage.getItem(STORAGE_KEYS.MUTE)
    if (mutePref !== null) {
      currentMuted.value = mutePref === 'true'
    }

    // 读取音量偏好
    const volumePref = localStorage.getItem(STORAGE_KEYS.VOLUME)
    if (volumePref !== null) {
      const vol = parseFloat(volumePref)
      if (!isNaN(vol) && vol >= 0 && vol <= 1) {
        currentVolume.value = vol
      }
    }
  } catch (e) {
    console.warn('[Shaka Player] Failed to load user preferences:', e)
  }
}

// 保存用户偏好设置到 localStorage
const saveUserPreference = (key, value) => {
  try {
    localStorage.setItem(key, String(value))
  } catch (e) {
    console.warn('[Shaka Player] Failed to save user preference:', e)
  }
}

// 配置 FairPlay DRM (Safari)
const configureFairPlay = (player, contentId) => {
  const { VITE_FAIRPLAY_LICENSE_URL, VITE_FAIRPLAY_CERT_URL } = import.meta.env
  player.configure({
    drm: {
      servers: {
        'com.apple.fps': VITE_FAIRPLAY_LICENSE_URL + contentId
      }
    }
  })

  const networkingEngine = player.getNetworkingEngine()
  networkingEngine.registerRequestFilter(async (type, request) => {
    if (type === shaka.net.NetworkingEngine.RequestType.SERVER_CERTIFICATE) {
      try {
        const response = await fetch(VITE_FAIRPLAY_CERT_URL)
        const certificate = await response.arrayBuffer()
        request.body = certificate
      } catch (error) {
        console.error('FairPlay certificate fetch failed:', error)
      }
    }
  })
}

// 配置 Widevine/PlayReady DRM (其他浏览器)
const configureStandardDRM = (player, contentId) => {
  const { VITE_WVPROPXY, VITE_PLAYREADY } = import.meta.env
  player.configure({
    drm: {
      servers: {
        'com.widevine.alpha': VITE_WVPROPXY + contentId,
        'com.microsoft.playready': VITE_PLAYREADY + contentId
      }
    }
  })
}

// 配置请求过滤器
const configureRequestFilter = (player) => {
  player.getNetworkingEngine().registerRequestFilter((type, request) => {
    if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
      if (request.headers.SOAPAction) {
        request.headers['Content-Type'] = 'text/xml'
      } else {
        request.headers['Content-Type'] = 'application/octet-stream'
      }
    }
  })
}

// 获取字幕 MIME 类型
const getMimeType = (url) => {
  if (!url) return 'application/ttml+xml'
  const extension = url.split('.').pop().toLowerCase()
  switch (extension) {
    case 'vtt':
      return 'text/vtt'
    case 'srt':
      return 'text/srt'
    case 'xml':
    case 'ttml':
      return 'application/ttml+xml'
    default:
      return 'application/ttml+xml'
  }
}

// 动态加载字幕
const loadSubtitleWithConversion = async (player, url, lang, kind, mime, label) => {
  try {
    console.log('addTextTrackAsync', url, lang, kind, mime, null, label)
    await player.addTextTrackAsync(url, lang, kind, mime, null, label)
    return true
  } catch (error) {
    console.error('[Shaka Player] Failed to load subtitle:', error)

    // Safari 浏览器降级方案：使用原生 track 元素加载字幕
    // 当 Shaka Player 的 addTextTrackAsync 失败时（常见于 Safari），使用原生 HTML5 track 元素
    if (isSafari() && url && url.includes('.vtt')) {
      console.log('[Shaka Player] Safari: Falling back to native track element')
      try {
        // 检查是否已存在相同属性的 track 元素，避免重复添加
        const existingTracks = videoPlayer.value.querySelectorAll('track')
        const isDuplicate = Array.from(existingTracks).some(
          (track) => track.src === url && track.srclang === lang && track.kind === kind
        )
        if (isDuplicate) {
          console.log('[Shaka Player] Safari: Track already exists, skipping addition')
          return true
        }

        const trackEl = document.createElement('track')
        trackEl.src = url
        trackEl.srclang = lang
        trackEl.label = label
        trackEl.kind = kind
        videoPlayer.value.appendChild(trackEl)
        return true
      } catch (fallbackError) {
        console.error('[Shaka Player] Safari: Native track fallback failed:', fallbackError)
        throw fallbackError
      }
    }

    throw error
  }
}

// 控制字幕可见性
const setSubtitleVisibility = (visible) => {
  if (!player) return

  // Shaka Player 5.x 使用 textDisplayer 控制字幕可见性
  // 通过配置 preferredTextLanguage 或操作 textTracks 来实现
  const textTracks = player.getTextTracks()
  if (textTracks.length > 0) {
    const activeTrack = textTracks.find((t) => t.active)
    if (activeTrack) {
      // 如果字幕轨道存在，通过 selectTextTrack(null) 隐藏，或重新选择来显示
      if (!visible) {
        player.selectTextTrack(null)
      }
    }
  }

  // 同时控制原生字幕轨道的可见性（用于 Safari 降级方案）
  if (isSafari() && videoPlayer.value && videoPlayer.value.textTracks) {
    const nativeTracks = videoPlayer.value.textTracks
    // 获取当前应该显示的语言代码
    const targetLangCode =
      currentSubtitle.value === 'tc' ? 'zh-Hant' : currentSubtitle.value === 'sc' ? 'zh-Hans' : null

    let hasActivatedTarget = false
    for (let i = 0; i < nativeTracks.length; i++) {
      const track = nativeTracks[i]
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        if (visible && targetLangCode && track.language === targetLangCode && !hasActivatedTarget) {
          // 只激活首个匹配目标语言的轨道
          track.mode = 'showing'
          hasActivatedTarget = true
        } else {
          // 强制隐藏所有非目标语言的轨道，以及多余的同语言轨道、无语言属性的内置轨道
          track.mode = 'hidden'
        }
      }
    }
  }
}

// 动态更新字幕样式（大小和位置）
const updateSubtitleStyle = () => {
  if (!player || !videoPlayer.value) return

  try {
    const config = player.getConfiguration()
    const scaleFactor = config.textDisplayer?.fontScaleFactor || 1
    const positionArea = config.textDisplayer?.positionArea || 0 // 0: default, 1: top-left, ..., 9: bottom-right

    // 检查是否处于全屏状态
    const isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    )

    // 检查是否为安卓设备
    const isAndroid = navigator.userAgent.toLowerCase().includes('android')

    // 1. 设置 CSS 变量，用于覆盖 Shaka UI 和原生字幕的大小
    // 基础字号：移动端 16px，桌面端 24px
    let baseFontSize = isMobile.value ? 16 : 24

    // 专门针对安卓设备全屏状态：字幕字号 24px
    if (isMobile.value && isAndroid && isFullscreen) {
      baseFontSize = 24
    }

    const newFontSize = Math.round(baseFontSize * scaleFactor)

    if (videoContainer.value) {
      videoContainer.value.style.setProperty('--subtitle-font-size', `${newFontSize}px`)
    }

    // 2. 针对使用原生字幕轨道的情况（例如 SimpleTextDisplayer 或 Safari 降级方案），手动更新每个 cue 的位置和大小
    if (videoPlayer.value.textTracks) {
      const nativeTracks = videoPlayer.value.textTracks
      for (let i = 0; i < nativeTracks.length; i++) {
        const track = nativeTracks[i]
        if (track.kind === 'subtitles' || track.kind === 'captions') {
          // 如果轨道已激活，更新其现有的 cues
          if (track.cues) {
            updateNativeCues(track.cues, positionArea, scaleFactor)
          }

          // 确保监听后续添加的 cues
          if (!track._cueChangeHandler) {
            track._cueChangeHandler = (e) => {
              if (!player) return
              try {
                const currentConfig = player.getConfiguration()
                updateNativeCues(
                  e.target.cues,
                  currentConfig.textDisplayer?.positionArea || 0,
                  currentConfig.textDisplayer?.fontScaleFactor || 1
                )
              } catch (err) {
                console.warn('[Shaka Player] Error in cuechange handler:', err)
              }
            }
            track.addEventListener('cuechange', track._cueChangeHandler)
          }
        }
      }
    }
  } catch (error) {
    console.error('[Shaka Player] Failed to update subtitle styles:', error)
  }
}

// 辅助函数：清理原生字幕轨道的事件监听器，防止内存泄漏
const cleanupSubtitleListeners = () => {
  if (!videoPlayer.value || !videoPlayer.value.textTracks) return

  const nativeTracks = videoPlayer.value.textTracks
  for (let i = 0; i < nativeTracks.length; i++) {
    const track = nativeTracks[i]
    if (track._cueChangeHandler) {
      track.removeEventListener('cuechange', track._cueChangeHandler)
      track._cueChangeHandler = null
    }
  }
}

// 辅助函数：更新 Safari 原生 VTTCue 的位置属性
const updateNativeCues = (cues, positionArea, scaleFactor) => {
  if (!cues) return

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]
    if (!cue) continue

    // 如果是移动端设备，强制字幕固定在底部偏上区域，且不受右键菜单方位影响
    if (isMobile.value) {
      cue.line = 92 // 位于屏幕底部约8%处，留给控制栏空间
      cue.snapToLines = false
      cue.position = 50
      cue.align = 'center'
      cue.size = 100 // 全宽以防多行文本溢出
      continue
    }

    // 以下为桌面端常规的 9宫格方位设定
    // 根据 positionArea 设置位置
    // 1: top-left, 2: top-center, 3: top-right
    // 4: center-left, 5: center, 6: center-right
    // 7: bottom-left, 8: bottom-center, 9: bottom-right, 0: default (bottom-center)

    // 设置垂直位置 (line)
    if ([1, 2, 3].includes(positionArea)) {
      cue.line = 5
      cue.snapToLines = false
    } else if ([4, 5, 6].includes(positionArea)) {
      cue.line = 50
      cue.snapToLines = false
    } else {
      cue.line = -3 // 底部默认位置
      cue.snapToLines = true
    }

    // 设置水平位置 (position) 和 对齐 (align)
    // 根据字号大小缩放调整最大宽度
    if ([1, 4, 7].includes(positionArea)) {
      cue.position = 5
      cue.align = 'start'
      cue.size = 90 // 防止文本靠边换行溢出
    } else if ([3, 6, 9].includes(positionArea)) {
      cue.position = 95
      cue.align = 'end'
      cue.size = 90
    } else {
      cue.position = 50 // 默认或居中
      cue.align = 'center'
      cue.size = 100
    }
  }
}

// 获取当前应该使用的视频源URL（根据分辨率设置）
const getCurrentVideoSrc = () => {
  if (!props.videoClarity) return props.src

  // 如果设置了对应分辨率的URL，使用它，否则使用默认src
  if (currentClarity.value === 'sd' && props.videoClarity.sd) {
    return props.videoClarity.sd
  } else if (currentClarity.value === 'hd' && props.videoClarity.hd) {
    return props.videoClarity.hd
  }
  return props.src
}

// 切换分辨率
const switchClarity = async (clarity) => {
  if (!player || !videoPlayer.value) return

  const wasPlaying = !videoPlayer.value.paused
  const currentTime = videoPlayer.value.currentTime

  try {
    currentClarity.value = clarity
    saveUserPreference(STORAGE_KEYS.CLARITY, clarity)

    // 获取新分辨率的视频源
    const newSrc = getCurrentVideoSrc()

    // 重新加载视频
    await player.load(newSrc, currentTime)

    // 恢复播放状态
    if (wasPlaying) {
      await videoPlayer.value.play()
    }

    // 重新应用用户偏好设置
    await applyUserPreferences()

    // 更新菜单显示
    updateMoreMenu()
    updateDesktopMenus()
  } catch (error) {
    console.error('[Shaka Player] Failed to switch clarity:', error)
  }
}

// 切换字幕
const switchSubtitle = async (subtitleType) => {
  if (!player || !videoPlayer.value) return

  try {
    currentSubtitle.value = subtitleType
    saveUserPreference(STORAGE_KEYS.SUBTITLE, subtitleType)

    if (subtitleType === 'off') {
      player.selectTextTrack(null)
      setSubtitleVisibility(false)
    } else {
      const subtitleUrl = subtitleType === 'tc' ? props.subtitle?.tc : props.subtitle?.sc
      if (subtitleUrl) {
        const mimeType = getMimeType(subtitleUrl)
        const langCode = subtitleType === 'tc' ? 'zh-Hant' : 'zh-Hans'

        const tracks = player.getTextTracks()
        const existingTrack = tracks.find((t) => t.language === langCode && t.kind === 'subtitles')

        if (!existingTrack) {
          await loadSubtitleWithConversion(
            player,
            subtitleUrl,
            langCode,
            'subtitles',
            mimeType,
            subtitleType === 'tc' ? '繁體中文' : '簡體中文'
          )
        }

        const updatedTracks = player.getTextTracks()
        const trackToSelect = updatedTracks.find(
          (t) => t.language === langCode && t.kind === 'subtitles'
        )

        console.log('trackToSelect', trackToSelect, updatedTracks)
        if (trackToSelect) {
          player.selectTextTrack(trackToSelect)
          setSubtitleVisibility(true)
        } else if (isSafari()) {
          // Safari 浏览器特殊处理：如果找不到 Shaka 轨道，尝试使用原生 track 元素
          // 检查是否已通过降级方案添加了原生字幕轨道，并隐藏非当前语言的字幕
          const nativeTracks = videoPlayer.value.textTracks
          let hasActivatedTarget = false
          for (let i = 0; i < nativeTracks.length; i++) {
            const track = nativeTracks[i]
            if (track.kind === 'subtitles' || track.kind === 'captions') {
              if (track.language === langCode && !hasActivatedTarget) {
                track.mode = 'showing'
                hasActivatedTarget = true
                console.log('[Shaka Player] Safari: Activated native track', track)
              } else {
                track.mode = 'hidden'
              }
            }
          }
        }
      }
    }

    updateMoreMenu()
    updateDesktopMenus()
  } catch (error) {
    console.error('[Shaka Player] Failed to switch subtitle:', error)
    currentSubtitle.value = 'off'
    player.selectTextTrack(null)
    setSubtitleVisibility(false)
    updateMoreMenu()
    updateDesktopMenus()
  }
}

// 切换播放速度
const switchPlaybackRate = async (rate) => {
  if (!videoPlayer.value) return

  try {
    currentPlaybackRate.value = rate
    saveUserPreference(STORAGE_KEYS.PLAYBACK_RATE, rate)
    videoPlayer.value.playbackRate = rate

    // Safari 浏览器特殊处理：确保在全屏模式下也能同步播放速度
    if (isSafari()) {
      // Safari 可能会在全屏切换时重置播放速度，需要延迟再次设置
      setTimeout(() => {
        if (videoPlayer.value && videoPlayer.value.playbackRate !== rate) {
          videoPlayer.value.playbackRate = rate
          console.log('[Shaka Player] Safari: Re-synced playback rate to', rate)
        }
      }, 100)

      // 检查是否处于全屏状态，如果是则再次确保速度同步
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      if (isFullscreen) {
        // 全屏状态下延迟设置，确保 Safari 原生控制器已更新
        setTimeout(() => {
          if (videoPlayer.value) {
            videoPlayer.value.playbackRate = rate
            console.log('[Shaka Player] Safari: Synced playback rate in fullscreen mode')
          }
        }, 50)
      }
    }

    // 更新菜单显示
    updateMoreMenu()
    updateDesktopMenus()
  } catch (error) {
    console.error('[Shaka Player] Failed to switch playback rate:', error)
    // 恢复默认速度
    videoPlayer.value.playbackRate = 1.0
    currentPlaybackRate.value = 1.0
    updateMoreMenu()
    updateDesktopMenus()
  }
}

// 创建自定义控制按钮
const createCustomButton = (className, ariaLabel, innerHTML, onClick) => {
  const button = document.createElement('button')
  button.className = `shaka-button shaka-custom-button shaka-tooltip ${className}`
  button.setAttribute('aria-label', ariaLabel)
  button.innerHTML = innerHTML
  button.style.cssText = `
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.9;
    font-size: 14px;
    min-width: 40px;
  `
  button.addEventListener('mouseenter', () => {
    button.style.opacity = '1'
  })
  button.addEventListener('mouseleave', () => {
    button.style.opacity = '0.9'
  })
  button.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    onClick(e)
  })
  return button
}

// ============================================
// 桌面端独立按钮实现
// ============================================

// 分辨率选择按钮和菜单（桌面端）
let clarityButton = null
let clarityMenu = null

// 添加分辨率选择按钮（桌面端）
const addClarityButton = (container) => {
  // 检查是否有有效的分辨率配置
  if (!props.videoClarity || (!props.videoClarity.sd && !props.videoClarity.hd)) return

  const controlPanel = container.querySelector('.shaka-controls-button-panel')
  if (!controlPanel) return

  // 动态构建分辨率选项
  const clarityOptions = []
  if (props.videoClarity.sd) {
    clarityOptions.push({ label: 'SD標清', value: 'sd' })
  }
  if (props.videoClarity.hd) {
    clarityOptions.push({ label: 'HD高清', value: 'hd' })
  }

  // 如果没有有效选项，不添加按钮
  if (clarityOptions.length === 0) return

  // 创建分辨率选择按钮
  clarityButton = createCustomButton(
    'shaka-clarity-button',
    '分辨率',
    currentClarity.value === 'sd' ? 'SD' : 'HD',
    () => {
      if (clarityMenu) {
        const isOpening = clarityMenu.style.display !== 'flex'
        // 关闭其他弹窗
        popupManager.closeAll(clarityMenu)
        // 切换当前弹窗
        clarityMenu.style.display = isOpening ? 'flex' : 'none'
      }
    }
  )

  // 创建分辨率选择菜单
  clarityMenu = createDropdownMenu(clarityOptions, currentClarity.value, (value) => {
    switchClarity(value)
    if (clarityMenu) clarityMenu.style.display = 'none'
  })

  clarityButton.appendChild(clarityMenu)
  controlPanel.appendChild(clarityButton)
}

// 字幕选择按钮和菜单（桌面端）
let subtitleButton = null
let subtitleMenu = null

// 添加字幕选择按钮（桌面端）
const addSubtitleButton = (container) => {
  // 检查是否有有效的字幕配置
  if (!props.subtitle || (!props.subtitle.tc && !props.subtitle.sc)) return

  const controlPanel = container.querySelector('.shaka-controls-button-panel')
  if (!controlPanel) return

  // 动态构建字幕选项
  const subtitleOptions = []
  if (props.subtitle.tc) {
    subtitleOptions.push({ label: '中文繁體', value: 'tc' })
  }
  if (props.subtitle.sc) {
    subtitleOptions.push({ label: '中文簡體', value: 'sc' })
  }
  subtitleOptions.push({ label: '關閉', value: 'off' })

  // 如果没有有效选项（只有"关闭"），不添加按钮
  if (subtitleOptions.length <= 1) return

  // 创建字幕选择按钮
  subtitleButton = createCustomButton(
    'shaka-subtitle-button',
    '字幕',
    `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 12h4v2H4v-2zm10 6H4v-2h10v2zm6 0h-4v-2h4v2zm0-4H10v-2h10v2z"/>
    </svg>`,
    () => {
      if (subtitleMenu) {
        const isOpening = subtitleMenu.style.display !== 'flex'
        // 关闭其他弹窗
        popupManager.closeAll(subtitleMenu)
        // 切换当前弹窗
        subtitleMenu.style.display = isOpening ? 'flex' : 'none'
      }
    }
  )

  // 创建字幕选择菜单
  subtitleMenu = createDropdownMenu(subtitleOptions, currentSubtitle.value, (value) => {
    switchSubtitle(value)
    if (subtitleMenu) subtitleMenu.style.display = 'none'
  })

  subtitleButton.appendChild(subtitleMenu)
  controlPanel.appendChild(subtitleButton)
}

// 播放速度选择按钮和菜单（桌面端）
let playbackRateButton = null
let playbackRateMenu = null

// 添加播放速度选择按钮（桌面端）
const addPlaybackRateButton = (container) => {
  const controlPanel = container.querySelector('.shaka-controls-button-panel')
  if (!controlPanel) return

  // 创建播放速度选择按钮
  playbackRateButton = createCustomButton(
    'shaka-playback-rate-button',
    '播放速度',
    `${currentPlaybackRate.value}x`,
    () => {
      if (playbackRateMenu) {
        const isOpening = playbackRateMenu.style.display !== 'flex'
        // 关闭其他弹窗
        popupManager.closeAll(playbackRateMenu)
        // 切换当前弹窗
        playbackRateMenu.style.display = isOpening ? 'flex' : 'none'
      }
    }
  )

  // 创建播放速度选择菜单
  playbackRateMenu = createDropdownMenu(
    [
      { label: '0.5', value: 0.5 },
      { label: '1.0', value: 1.0 },
      { label: '1.5', value: 1.5 },
      { label: '2.0', value: 2.0 }
    ],
    currentPlaybackRate.value,
    (value) => {
      switchPlaybackRate(value)
      if (playbackRateMenu) playbackRateMenu.style.display = 'none'
    }
  )

  playbackRateButton.appendChild(playbackRateMenu)
  controlPanel.appendChild(playbackRateButton)
}

// ============================================
// Mobile "More" Menu Implementation (Hierarchical Navigation)
// ============================================

// More menu related variables
let moreButton = null
let moreMenu = null
let moreMenuContent = null
let currentMenuLevel = 'main' // 'main' | 'clarity' | 'subtitle' | 'playbackRate'

// Get current status display text
const getClarityLabel = () => (currentClarity.value === 'sd' ? 'SD' : 'HD')
const getSubtitleLabel = () => {
  const labels = { tc: '中文繁體', sc: '中文簡體', off: '關閉' }
  return labels[currentSubtitle.value] || '中文'
}
const getPlaybackRateLabel = () => `${currentPlaybackRate.value}x`

// Update "More" menu display
const updateMoreMenu = () => {
  if (!moreMenuContent) return

  // Render content based on current menu level
  if (currentMenuLevel === 'main') {
    renderMainMenu()
  } else if (currentMenuLevel === 'clarity') {
    renderClaritySubmenu()
  } else if (currentMenuLevel === 'subtitle') {
    renderSubtitleSubmenu()
  } else if (currentMenuLevel === 'playbackRate') {
    renderPlaybackRateSubmenu()
  }
}

// 渲染主菜单
const renderMainMenu = () => {
  if (!moreMenuContent) return

  currentMenuLevel = 'main'
  moreMenuContent.innerHTML = ''
  moreMenuContent.className = 'more-menu-content more-menu-main'

  // 分辨率菜单项 - 仅当有有效分辨率选项时显示
  if (props.videoClarity && (props.videoClarity.sd || props.videoClarity.hd)) {
    const clarityItem = createMoreMenuItem('more-menu-clarity', '分辨率', getClarityLabel(), () => {
      currentMenuLevel = 'clarity'
      renderClaritySubmenu()
    })
    moreMenuContent.appendChild(clarityItem)
  }

  // 字幕菜单项 - 仅当有有效字幕选项时显示
  if (props.subtitle && (props.subtitle.tc || props.subtitle.sc)) {
    const subtitleItem = createMoreMenuItem(
      'more-menu-subtitle',
      '字幕',
      getSubtitleLabel(),
      () => {
        currentMenuLevel = 'subtitle'
        renderSubtitleSubmenu()
      }
    )
    moreMenuContent.appendChild(subtitleItem)
  }

  // 播放速度菜单项
  const playbackRateItem = createMoreMenuItem(
    'more-menu-playback-rate',
    '播放速度',
    getPlaybackRateLabel(),
    () => {
      currentMenuLevel = 'playbackRate'
      renderPlaybackRateSubmenu()
    }
  )
  moreMenuContent.appendChild(playbackRateItem)
}

// 渲染分辨率子菜单
const renderClaritySubmenu = () => {
  if (!moreMenuContent) return

  moreMenuContent.innerHTML = ''
  moreMenuContent.className = 'more-menu-content more-menu-submenu'

  // 创建子菜单头部（返回按钮+标题）
  const header = createSubmenuHeader('分辨率', () => {
    renderMainMenu()
  })
  moreMenuContent.appendChild(header)

  // 动态创建分辨率选项
  const options = []
  if (props.videoClarity?.sd) {
    options.push({ label: 'SD標清', value: 'sd' })
  }
  if (props.videoClarity?.hd) {
    options.push({ label: 'HD高清', value: 'hd' })
  }

  options.forEach((option) => {
    const isActive = currentClarity.value === option.value
    const item = createSubmenuOption(option.label, isActive, () => {
      switchClarity(option.value)
      closeMoreMenu()
    })
    moreMenuContent.appendChild(item)
  })
}

// 渲染字幕子菜单
const renderSubtitleSubmenu = () => {
  if (!moreMenuContent) return

  moreMenuContent.innerHTML = ''
  moreMenuContent.className = 'more-menu-content more-menu-submenu'

  // 创建子菜单头部（返回按钮+标题）
  const header = createSubmenuHeader('字幕', () => {
    renderMainMenu()
  })
  moreMenuContent.appendChild(header)

  // 动态创建字幕选项
  const options = []
  if (props.subtitle?.tc) {
    options.push({ label: '中文繁體', value: 'tc' })
  }
  if (props.subtitle?.sc) {
    options.push({ label: '中文簡體', value: 'sc' })
  }
  options.push({ label: '關閉', value: 'off' })

  options.forEach((option) => {
    const isActive = currentSubtitle.value === option.value
    const item = createSubmenuOption(option.label, isActive, () => {
      switchSubtitle(option.value)
      closeMoreMenu()
    })
    moreMenuContent.appendChild(item)
  })
}

// 渲染播放速度子菜单
const renderPlaybackRateSubmenu = () => {
  if (!moreMenuContent) return

  moreMenuContent.innerHTML = ''
  moreMenuContent.className = 'more-menu-content more-menu-submenu'

  // 创建子菜单头部（返回按钮+标题）
  const header = createSubmenuHeader('播放速度', () => {
    renderMainMenu()
  })
  moreMenuContent.appendChild(header)

  // 创建选项
  const options = [
    { label: '0.5x', value: 0.5 },
    { label: '1.0x', value: 1.0 },
    { label: '1.5x', value: 1.5 },
    { label: '2.0x', value: 2.0 }
  ]

  options.forEach((option) => {
    const isActive = currentPlaybackRate.value === option.value
    const item = createSubmenuOption(option.label, isActive, () => {
      switchPlaybackRate(option.value)
      closeMoreMenu()
    })
    moreMenuContent.appendChild(item)
  })
}

// 创建"更多"菜单项（主菜单用）
const createMoreMenuItem = (className, label, valueLabel, onClick) => {
  const item = document.createElement('div')
  item.className = `more-menu-item ${className}`
  item.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    color: white;
    cursor: pointer;
    font-size: 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    transition: background 0.2s ease;
  `
  item.innerHTML = `
    <span class="more-menu-item-text">${label}</span>
    <div style="display: flex; align-items: center; gap: 4px;">
      <span class="more-menu-item-label" style="color: #00d4ff; font-size: 12px;">${valueLabel}</span>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style="color: rgba(255,255,255,0.5);">
        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
      </svg>
    </div>
  `
  item.addEventListener('mouseenter', () => {
    item.style.background = 'rgba(255, 255, 255, 0.1)'
  })
  item.addEventListener('mouseleave', () => {
    item.style.background = 'transparent'
  })
  item.addEventListener('click', (e) => {
    e.stopPropagation()
    onClick()
  })
  return item
}

// 创建子菜单头部（返回按钮+标题）
const createSubmenuHeader = (title, onBack) => {
  const header = document.createElement('div')
  header.className = 'more-submenu-header'
  header.style.cssText = `
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
  `

  // 返回按钮
  const backButton = document.createElement('button')
  backButton.className = 'more-submenu-back'
  backButton.style.cssText = `
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
  `
  backButton.innerHTML = `
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
    </svg>
  `
  backButton.addEventListener('click', (e) => {
    e.stopPropagation()
    onBack()
  })

  // 标题
  const titleSpan = document.createElement('span')
  titleSpan.className = 'more-submenu-title'
  titleSpan.textContent = title
  titleSpan.style.cssText = `
    color: white;
    font-size: 14px;
    font-weight: 500;
    flex: 1;
    text-align: center;
  `

  header.appendChild(backButton)
  header.appendChild(titleSpan)

  return header
}

// 创建子菜单选项
const createSubmenuOption = (label, isActive, onClick) => {
  const option = document.createElement('div')
  option.className = `more-submenu-option ${isActive ? 'active' : ''}`
  option.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    color: ${isActive ? '#00d4ff' : 'white'};
    cursor: pointer;
    font-size: 14px;
    background: ${isActive ? 'rgba(0, 212, 255, 0.1)' : 'transparent'};
    transition: background 0.2s ease;
  `
  option.innerHTML = `
    <span>${label}</span>
    ${isActive ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>' : ''}
  `
  option.addEventListener('mouseenter', () => {
    if (!isActive) {
      option.style.background = 'rgba(255, 255, 255, 0.1)'
    }
  })
  option.addEventListener('mouseleave', () => {
    if (!isActive) {
      option.style.background = 'transparent'
    }
  })
  option.addEventListener('click', (e) => {
    e.stopPropagation()
    onClick()
  })
  return option
}

// 创建"更多"菜单
const createMoreMenu = () => {
  const menu = document.createElement('div')
  menu.className = 'shaka-more-menu'

  menu.style.cssText = `
    position: absolute;
    bottom: 60px;
    right: 16px;
    background: rgba(0, 0, 0, 0.95);
    border-radius: 8px;
    min-width: 180px;
    max-height: 70vh;
    display: none;
    flex-direction: column;
    z-index: 1000;
    margin-bottom: 8px;
    height: fit-content;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    animation: slideUp 0.2s ease-out;
  `

  // 创建内容容器
  moreMenuContent = document.createElement('div')
  moreMenuContent.className = 'more-menu-content'
  moreMenuContent.style.cssText = `
    display: flex;
    flex-direction: column;
    max-height: calc(70vh - 16px);
    overflow-y: auto;
    padding: 8px 0;
  `

  menu.appendChild(moreMenuContent)

  // 初始渲染主菜单
  renderMainMenu()

  return menu
}

// 关闭"更多"菜单（带平滑动画）
const closeMoreMenu = () => {
  if (moreMenu) {
    // 添加淡出动画类
    moreMenu.style.animation = 'slideDown 0.2s ease-out forwards'
    // 动画结束后隐藏菜单
    setTimeout(() => {
      moreMenu.style.display = 'none'
      moreMenu.style.animation = ''
    }, 200)
  }
  // 重置到主菜单级别
  currentMenuLevel = 'main'
}

// 添加"更多"按钮（移动端）
const addMoreButton = (container) => {
  const controlPanel = container.querySelector('.shaka-controls-button-panel')
  if (!controlPanel) return

  // 创建"更多"按钮（三点图标）
  moreButton = createCustomButton(
    'shaka-more-button',
    '更多',
    `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
    </svg>`,
    () => {
      if (moreMenu) {
        const isVisible = moreMenu.style.display !== 'none' && moreMenu.style.display !== ''
        if (isVisible) {
          closeMoreMenu()
        } else {
          moreMenu.style.display = 'flex'
          updateMoreMenu()
        }
      }
    }
  )

  // 创建"更多"菜单
  moreMenu = createMoreMenu()
  controlPanel.appendChild(moreMenu)
  controlPanel.appendChild(moreButton)
}

// ============================================
// 通用工具函数
// ============================================

// 弹窗管理器 - 用于管理所有桌面端弹窗的互斥显示
const popupManager = {
  popups: [],
  register(popup) {
    this.popups.push(popup)
  },
  unregister(popup) {
    this.popups = this.popups.filter((p) => p !== popup)
  },
  closeAll(except = null) {
    this.popups.forEach((popup) => {
      if (popup !== except && popup.style.display === 'flex') {
        popup.style.display = 'none'
      }
    })
  }
}

// 创建下拉菜单（通用）
const createDropdownMenu = (items, currentValue, onSelect) => {
  const menu = document.createElement('div')
  menu.className = 'shaka-custom-dropdown'
  menu.style.cssText = `
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.85);
    border-radius: 8px;
    padding: 8px 0;
    min-width: 120px;
    display: none;
    flex-direction: column;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    animation: fadeInUp 0.2s ease-out;
  `

  // 注册到弹窗管理器
  popupManager.register(menu)

  items.forEach((item) => {
    const option = document.createElement('div')
    option.className = 'shaka-custom-dropdown-item'
    option.textContent = item.label
    option.style.cssText = `
      padding: 10px 16px;
      color: ${item.value === currentValue ? '#00d4ff' : 'white'};
      cursor: pointer;
      font-size: 14px;
      white-space: nowrap;
      transition: background 0.15s ease;
    `
    option.addEventListener('mouseenter', () => {
      option.style.background = 'rgba(255, 255, 255, 0.1)'
    })
    option.addEventListener('mouseleave', () => {
      option.style.background = 'transparent'
    })
    option.addEventListener('click', (e) => {
      e.stopPropagation()
      onSelect(item.value)
      menu.style.display = 'none'
    })
    menu.appendChild(option)
  })

  return menu
}

// 更新桌面端菜单显示
const updateDesktopMenus = () => {
  if (isMobile.value) return

  // 构建分辨率菜单选项
  const clarityOptions = []
  if (props.videoClarity?.sd) {
    clarityOptions.push({ label: 'SD標清', value: 'sd' })
  }
  if (props.videoClarity?.hd) {
    clarityOptions.push({ label: 'HD高清', value: 'hd' })
  }

  if (clarityButton && clarityMenu && clarityOptions.length > 0) {
    clarityButton.innerHTML = currentClarity.value === 'sd' ? 'SD' : 'HD'
    popupManager.unregister(clarityMenu)
    clarityMenu.remove()
    clarityMenu = createDropdownMenu(clarityOptions, currentClarity.value, (value) => {
      switchClarity(value)
      if (clarityMenu) clarityMenu.style.display = 'none'
    })
    clarityButton.appendChild(clarityMenu)
  }

  // 构建字幕菜单选项
  const subtitleOptions = []
  if (props.subtitle?.tc) {
    subtitleOptions.push({ label: '中文繁體', value: 'tc' })
  }
  if (props.subtitle?.sc) {
    subtitleOptions.push({ label: '中文簡體', value: 'sc' })
  }
  subtitleOptions.push({ label: '關閉', value: 'off' })

  if (subtitleButton && subtitleMenu && subtitleOptions.length > 1) {
    popupManager.unregister(subtitleMenu)
    subtitleMenu.remove()
    subtitleMenu = createDropdownMenu(subtitleOptions, currentSubtitle.value, (value) => {
      switchSubtitle(value)
      if (subtitleMenu) subtitleMenu.style.display = 'none'
    })
    subtitleButton.appendChild(subtitleMenu)
  }

  if (playbackRateButton && playbackRateMenu) {
    playbackRateButton.innerHTML = `${currentPlaybackRate.value}x`
    popupManager.unregister(playbackRateMenu)
    playbackRateMenu.remove()
    playbackRateMenu = createDropdownMenu(
      [
        { label: '0.5', value: 0.5 },
        { label: '1.0', value: 1.0 },
        { label: '1.5', value: 1.5 },
        { label: '2.0', value: 2.0 }
      ],
      currentPlaybackRate.value,
      (value) => {
        switchPlaybackRate(value)
        if (playbackRateMenu) playbackRateMenu.style.display = 'none'
      }
    )
    playbackRateButton.appendChild(playbackRateMenu)
  }
}

// 点击外部关闭菜单（支持触摸和点击事件）
const handleClickOutside = (e) => {
  // 桌面端菜单
  if (clarityMenu && clarityButton && !clarityButton.contains(e.target)) {
    clarityMenu.style.display = 'none'
  }
  if (subtitleMenu && subtitleButton && !subtitleButton.contains(e.target)) {
    subtitleMenu.style.display = 'none'
  }
  if (playbackRateMenu && playbackRateButton && !playbackRateButton.contains(e.target)) {
    playbackRateMenu.style.display = 'none'
  }

  // 移动端"更多"菜单 - 增强检测逻辑
  if (moreMenu && moreButton) {
    const isClickOnButton = moreButton.contains(e.target)
    const isClickOnMenu = moreMenu.contains(e.target)

    // 如果点击既不在按钮上也不在菜单上，关闭菜单
    if (!isClickOnButton && !isClickOnMenu) {
      closeMoreMenu()
    }
  }
}

// 窗口大小变化处理
const handleResize = () => {
  const wasMobile = isMobile.value
  checkMobile()

  // 如果移动端状态发生变化，需要重新初始化控制栏
  if (wasMobile !== isMobile.value && videoContainer.value) {
    // 清除现有按钮
    removeCustomButtons()
    // 重新添加按钮
    addControlButtons(videoContainer.value)
    // 更新字幕样式，基准字号可能变化
    updateSubtitleStyle()
  }
}

// 移除自定义按钮
const removeCustomButtons = () => {
  // 移除桌面端按钮
  if (clarityButton) {
    clarityButton.remove()
    clarityButton = null
    clarityMenu = null
  }
  if (subtitleButton) {
    subtitleButton.remove()
    subtitleButton = null
    subtitleMenu = null
  }
  if (playbackRateButton) {
    playbackRateButton.remove()
    playbackRateButton = null
    playbackRateMenu = null
  }

  // 移除移动端按钮
  if (moreButton) {
    moreButton.remove()
    moreButton = null
    moreMenu = null
    moreMenuContent = null
    currentMenuLevel = 'main'
  }
}

// 添加控制按钮（根据移动端状态决定）
const addControlButtons = (container) => {
  if (isMobile.value) {
    // 移动端：显示"更多"菜单按钮
    addMoreButton(container)
  } else {
    // 桌面端：显示独立按钮
    if (props.videoClarity) {
      addClarityButton(container)
    }
    if (props.subtitle) {
      addSubtitleButton(container)
    }
    addPlaybackRateButton(container)
  }
}

// 初始化 UI
const initUI = (player, container, video) => {
  console.log('[Shaka Player] initUI called, controls:', props?.playerVars?.controls)

  if (props?.playerVars?.controls === 0) {
    console.log('[Shaka Player] Controls disabled, skipping UI init')
    return
  }

  if (!container) {
    console.warn('Shaka Player: Video container not available for UI overlay')
    return
  }

  // 检测是否为移动端
  checkMobile()

  // 检测是否需要显示上一集/下一集按钮
  // 条件1：浏览器支持 Shaka UI（Safari 17 以下不支持）
  // 条件2：showEpisodeNavigation props 为 true（默认 true，可通过 v-bind 控制）
  const showPrevNextButtons = hasShakaUI() && props.showEpisodeNavigation
  console.log('[Shaka Player] showPrevNextButtons:', showPrevNextButtons)
  console.log('[Shaka Player] hasShakaUI():', hasShakaUI())
  console.log('[Shaka Player] showEpisodeNavigation:', props.showEpisodeNavigation)
  console.log('[Shaka Player] isMobile:', isMobile.value)

  // 配置控制栏元素 - 移动端隐藏音量控制
  const controlPanelElements = isMobile.value
    ? ['play_pause', 'time_and_duration', 'spacer', 'mute', 'fullscreen']
    : ['play_pause', 'time_and_duration', 'spacer', 'volume', 'mute', 'fullscreen']

  ui = new shaka.ui.Overlay(player, container, video)

  // 获取当前 UI 默认配置
  const currentUiConfig = ui.getConfiguration()
  // 保留所有右键菜单选项（包括循环播放和画中画）
  const contextMenuElements = currentUiConfig.contextMenuElements || [
    'captions-position',
    'captions-size',
    'loop',
    'picture_in_picture',
    'copy_video_frame',
    'save_video_frame'
  ]

  ui.configure({
    controlPanelElements,
    contextMenuElements
  })

  // 将整体 Shaka 播放器 UI（包括右键菜单）语言强制设置为繁体中文 (zh-TW)
  const controls = ui.getControls()
  if (controls && controls.getLocalization) {
    const localization = controls.getLocalization()
    if (localization) {
      localization.changeLocale(['zh-TW'])
    }
  }

  // 添加自定义上一集/下一集按钮
  if (showPrevNextButtons) {
    console.log('[Shaka Player] Calling addCustomPrevNextButtons...')
    addCustomPrevNextButtons(container)
  } else {
    console.log('[Shaka Player] Skipping custom buttons')
  }

  // 添加控制按钮（根据移动端状态）
  addControlButtons(container)

  // 添加点击外部关闭菜单的事件监听（支持触摸和点击）
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('touchstart', handleClickOutside, { passive: true })

  // 监听窗口大小变化
  window.addEventListener('resize', handleResize)

  const header = document.createElement('div')
  header.classList.add('tvb-player-shaka-header')
  header.innerHTML = "<div class='tvb-player-shaka-header-title'>" + props.title + '</div>'
  const controlsContainer = container.querySelector('.shaka-controls-container')
  if (controlsContainer) {
    controlsContainer.appendChild(header)
  }

  // 使用 Shaka 内置 AdManager API 初始化广告（替代手动 DOM 插入）
  initAds()
}

// 添加自定义上一集/下一集按钮
const addCustomPrevNextButtons = (container) => {
  // 延迟添加，确保 Shaka UI 已完全渲染且播放器准备就绪
  setTimeout(() => {
    console.log('[Shaka Player] Adding custom prev/next buttons...')
    const controlsContainer = container.querySelector('.shaka-controls-container')
    // Shaka Player 使用 .shaka-controls-button-panel 作为按钮容器
    const controlPanel = container.querySelector('.shaka-controls-button-panel')

    console.log('[Shaka Player] controlsContainer:', controlsContainer)
    console.log('[Shaka Player] controlPanel (.shaka-controls-button-panel):', controlPanel)

    if (!controlsContainer || !controlPanel) {
      console.warn('[Shaka Player] Control panel not found, skipping button addition')
      // 尝试查找所有可能的控制面板类名
      const allPanels = container.querySelectorAll('[class*="control"]')
      console.log('[Shaka Player] All control elements:', allPanels)
      return
    }

    // 创建上一集按钮
    const prevButton = document.createElement('button')
    prevButton.className = 'shaka-button shaka-custom-button shaka-tooltip shaka-prev-button'
    prevButton.setAttribute('aria-label', '上一集')
    prevButton.innerHTML = `
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
      </svg>
    `
    prevButton.style.cssText = `
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.9;
    `
    prevButton.addEventListener('mouseenter', () => {
      prevButton.style.opacity = '1'
    })
    prevButton.addEventListener('mouseleave', () => {
      prevButton.style.opacity = '0.9'
    })
    prevButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      emits('prev')
    })

    // 创建下一集按钮
    const nextButton = document.createElement('button')
    nextButton.className = 'shaka-button shaka-custom-button shaka-tooltip shaka-next-button'
    nextButton.setAttribute('aria-label', '下一集')
    nextButton.innerHTML = `
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
      </svg>
    `
    nextButton.style.cssText = `
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.9;
    `
    nextButton.addEventListener('mouseenter', () => {
      nextButton.style.opacity = '1'
    })
    nextButton.addEventListener('mouseleave', () => {
      nextButton.style.opacity = '0.9'
    })
    nextButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      emits('next')
    })

    // 将按钮插入到控制面板中
    // 按钮顺序：上一集 → 播放/暂停 → 下一集
    const playButton = controlPanel.querySelector('.shaka-play-button')
    console.log('[Shaka Player] playButton:', playButton)

    try {
      if (playButton) {
        // 在播放按钮之前插入上一集按钮
        playButton.parentNode.insertBefore(prevButton, playButton)
        // 在播放按钮之后插入下一集按钮
        playButton.parentNode.insertBefore(nextButton, playButton.nextSibling)
        console.log('[Shaka Player] Buttons inserted: Prev -> Play -> Next')
      } else {
        // 找不到播放按钮，按顺序添加到控制面板开头
        controlPanel.insertBefore(prevButton, controlPanel.firstChild)
        controlPanel.insertBefore(nextButton, prevButton.nextSibling)
        console.log('[Shaka Player] Prev and Next buttons inserted at beginning')
      }

      console.log('[Shaka Player] Buttons added successfully')
    } catch (err) {
      console.error('[Shaka Player] Error adding buttons:', err)
    }
  }, 800)
}

const eventListeners = []
const addEventListener = (element, event, handler) => {
  element.addEventListener(event, handler)
  eventListeners.push({ element, event, handler })
}

const removeAllEventListeners = () => {
  eventListeners.forEach(({ element, event, handler }) => {
    element.removeEventListener(event, handler)
  })
  eventListeners.length = 0
  // 移除点击外部关闭菜单的事件监听
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('touchstart', handleClickOutside)
  // 移除窗口大小变化监听
  window.removeEventListener('resize', handleResize)
}

// 应用用户偏好设置到播放器
const applyUserPreferences = async () => {
  if (!videoPlayer.value || !player) return

  // 应用播放速度设置，添加错误处理和日志
  try {
    const targetRate = currentPlaybackRate.value
    videoPlayer.value.playbackRate = targetRate
    console.log('[Shaka Player] Applied playback rate:', targetRate)

    // Safari 浏览器特殊处理：延迟验证播放速度是否成功应用
    if (isSafari()) {
      setTimeout(() => {
        if (videoPlayer.value && videoPlayer.value.playbackRate !== targetRate) {
          console.warn(
            '[Shaka Player] Safari: Playback rate not applied, retrying...',
            'Current:',
            videoPlayer.value.playbackRate,
            'Target:',
            targetRate
          )
          videoPlayer.value.playbackRate = targetRate
        }
      }, 100)
    }
  } catch (error) {
    console.error('[Shaka Player] Failed to apply playback rate:', error)
    // 尝试恢复默认速度
    try {
      videoPlayer.value.playbackRate = 1.0
      currentPlaybackRate.value = 1.0
    } catch (fallbackError) {
      console.error('[Shaka Player] Failed to fallback to default rate:', fallbackError)
    }
  }

  // 应用字幕偏好设置，并为原生播放器预加载所有可用字幕
  if (props.subtitle) {
    try {
      // 1. 预加载所有可用的字幕轨道 (主要为了 iOS 原生播放器菜单能显示所有选项)
      const tracksToLoad = []
      if (props.subtitle.tc) {
        tracksToLoad.push({
          url: props.subtitle.tc,
          langCode: 'zh-Hant',
          label: '繁體中文'
        })
      }
      if (props.subtitle.sc) {
        tracksToLoad.push({
          url: props.subtitle.sc,
          langCode: 'zh-Hans',
          label: '簡體中文'
        })
      }

      // 遍历加载所有字幕
      for (const trackInfo of tracksToLoad) {
        const mimeType = getMimeType(trackInfo.url)
        const existingTrack = player
          .getTextTracks()
          .find((t) => t.language === trackInfo.langCode && t.kind === 'subtitles')

        if (!existingTrack) {
          await loadSubtitleWithConversion(
            player,
            trackInfo.url,
            trackInfo.langCode,
            'subtitles',
            mimeType,
            trackInfo.label
          )
        }
      }

      // 2. 根据用户当前的偏好设置激活对应的字幕
      if (currentSubtitle.value !== 'off') {
        const targetLangCode = currentSubtitle.value === 'tc' ? 'zh-Hant' : 'zh-Hans'
        const updatedTracks = player.getTextTracks()
        const trackToSelect = updatedTracks.find(
          (t) => t.language === targetLangCode && t.kind === 'subtitles'
        )

        if (trackToSelect) {
          player.selectTextTrack(trackToSelect)
          setSubtitleVisibility(true)
        } else if (isSafari()) {
          // Safari 浏览器特殊处理：如果找不到 Shaka 轨道，尝试使用原生 track 元素
          const nativeTracks = videoPlayer.value.textTracks
          let hasActivatedTarget = false
          for (let i = 0; i < nativeTracks.length; i++) {
            const track = nativeTracks[i]
            if (track.kind === 'subtitles' || track.kind === 'captions') {
              if (track.language === targetLangCode && !hasActivatedTarget) {
                track.mode = 'showing'
                hasActivatedTarget = true
                console.log(
                  '[Shaka Player] Safari: Activated native track in applyUserPreferences',
                  track
                )
              } else {
                track.mode = 'hidden'
              }
            }
          }
        }
      } else {
        // 用户偏好为关闭字幕
        player.selectTextTrack(null)
        setSubtitleVisibility(false)
      }
    } catch (e) {
      console.warn('[Shaka Player] Failed to apply subtitle preference:', e)
    }
  } else if (player) {
    player.selectTextTrack(null)
    setSubtitleVisibility(false)
  }

  // 应用静音偏好设置
  if (videoPlayer.value) {
    videoPlayer.value.muted = currentMuted.value
    console.log('[Shaka Player] Applied mute preference:', currentMuted.value)
  }

  // 应用音量偏好设置
  if (videoPlayer.value) {
    videoPlayer.value.volume = currentVolume.value
    console.log('[Shaka Player] Applied volume preference:', currentVolume.value)
  }
}

const initPlayer = async () => {
  if (player) return
  if (initPromise) return initPromise

  // 加载用户偏好设置
  loadUserPreferences()

  initPromise = (async () => {
    try {
      // 等待 video 元素就绪
      const timeout = 2000
      const pollInterval = 50
      let elapsedTime = 0

      while ((!videoPlayer.value || !videoContainer.value) && elapsedTime < timeout) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval))
        elapsedTime += pollInterval
      }

      if (!videoPlayer.value || !videoContainer.value) {
        console.error('Shaka Player: Video element or container not found after timeout.')
        emits('stateChange', { state: 'error' })
        return
      }

      shaka.polyfill.installAll()
      player = new shaka.Player()

      // 先配置播放器（在 attach 之前）
      if (isSafari()) {
        // 获取当前字幕语言代码（根据用户偏好设置）
        const preferredSubtitleLang = currentSubtitle.value === 'sc' ? 'zh-Hans' : 'zh-Hant'

        // Safari 特定优化配置 - 必须在 attach 之前配置
        player.configure({
          streaming: {
            // useNativeHlsOnSafari: true,
            bufferingGoal: 60,
            rebufferingGoal: 15,
            stallEnabled: false, // 禁用 Shaka 的卡顿检测，避免与 Safari 原生行为冲突
            inaccurateManifestTolerance: 2
          },
          manifest: {
            defaultPresentationDelay: 0
          },
          // 配置首选字幕语言，使 Shaka Player 自动选择该语言的字幕轨道
          preferredTextLanguage: preferredSubtitleLang
        })
      } else {
        // 非 Safari 浏览器（Chrome、Firefox 等）的基础配置
        player.configure({
          streaming: {
            bufferingGoal: 30,
            rebufferingGoal: 10
          },
          manifest: {
            retryParameters: {
              maxAttempts: 3,
              baseDelay: 1000,
              backoffFactor: 2,
              fuzzFactor: 0.5
            }
          }
        })
      }

      await player.attach(videoPlayer.value)

      // 配置 DRM（在 attach 之后，load 之前）
      if (isSafari()) {
        configureFairPlay(player, props.contentId)
      } else {
        configureStandardDRM(player, props.contentId)
      }

      configureRequestFilter(player)

      // 获取当前应该使用的视频源（根据分辨率设置）
      const videoSrc = getCurrentVideoSrc()

      // 加载媒体（在 UI 初始化之前）
      await player.load(videoSrc)

      // 应用用户偏好设置
      await applyUserPreferences()

      // 初始化 UI
      if (isSafariBelow17()) {
        // Safari 17 以下版本使用原生 HTML5 控件，避免与 Shaka UI 冲突
        videoPlayer.value.controls = true
      } else {
        // Safari 17+ 和其他浏览器使用 Shaka UI
        initUI(player, videoContainer.value, videoPlayer.value)
      }

      // 绑定事件监听
      let lastState = null

      ;['play', 'pause', 'ended', 'error', 'waiting', 'playing'].forEach((e) => {
        addEventListener(videoPlayer.value, e, () => {
          // 白名单拦截停止广告期间，忽略 error 事件，避免误判为播放错误
          if (e === 'error' && isAdStoppingByWhitelist) {
            console.log('[Shaka Player] 忽略白名单拦截期间的 error 事件')
            return
          }

          // 将 'playing' 事件映射为 'play' 状态
          // 自动播放时，浏览器可能只触发 'playing' 事件而不触发 'play' 事件
          const emitState = e === 'playing' ? 'play' : e

          // 避免重复发送相同状态，但立即发送（不防抖）
          if (lastState !== emitState) {
            lastState = emitState
            emits('stateChange', { state: emitState })
          }

          // 视频结束时通知 InstreamAds 触发 Post-roll 广告
          if (emitState === 'ended') {
            onContentEnded()
          }
        })
      })

      // 监听播放速度变化事件
      addEventListener(videoPlayer.value, 'ratechange', () => {
        try {
          const newRate = videoPlayer.value.playbackRate
          // 忽略播放器在未播放或缓冲时将速度重置为 0 的情况
          if (newRate === 0) return
          // 更新当前播放速度状态
          currentPlaybackRate.value = newRate
          // 保存用户偏好
          saveUserPreference(STORAGE_KEYS.PLAYBACK_RATE, newRate)
          // 更新菜单显示
          updateMoreMenu()
          updateDesktopMenus()
          console.log('[Shaka Player] Playback rate changed to:', newRate)
        } catch (error) {
          console.error('[Shaka Player] Failed to handle rate change:', error)
        }
      })

      // 监听音量/静音状态变化事件，保存用户偏好
      addEventListener(videoPlayer.value, 'volumechange', () => {
        try {
          // 同步静音状态
          const newMuted = videoPlayer.value.muted
          currentMuted.value = newMuted
          saveUserPreference(STORAGE_KEYS.MUTE, newMuted)

          // 同步音量大小
          const newVolume = videoPlayer.value.volume
          currentVolume.value = newVolume
          saveUserPreference(STORAGE_KEYS.VOLUME, newVolume)

          console.log('[Shaka Player] Volume changed - muted:', newMuted, 'volume:', newVolume)
        } catch (error) {
          console.error('[Shaka Player] Failed to handle volume change:', error)
        }
      })

      // 监听配置变更事件，更新字幕样式 (大小、位置)
      player.addEventListener('configurationchanged', () => {
        updateSubtitleStyle()
      })

      // 监听轨道变更事件，确保切换字幕也能应用正确样式
      player.addEventListener('trackschanged', () => {
        updateSubtitleStyle()
      })

      // 监听全屏状态变化，确保播放速度同步及字幕大小缩放重算
      const handleFullscreenChange = () => {
        try {
          const isFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
          )

          // 重新评估移动端横竖屏的字幕放大比例
          updateSubtitleStyle()

          if (videoPlayer.value) {
            // 进入全屏时，确保播放速度同步
            if (isFullscreen) {
              const targetRate = currentPlaybackRate.value
              if (videoPlayer.value.playbackRate !== targetRate) {
                videoPlayer.value.playbackRate = targetRate
                console.log('[Shaka Player] Synced playback rate in fullscreen:', targetRate)
              }
            }

            // 无论是进入还是退出全屏，重新强制校验字幕可见性
            // 延迟100ms执行，确保系统的默认全屏行为已经结束
            setTimeout(() => {
              setSubtitleVisibility(currentSubtitle.value !== 'off')
            }, 100)
          }
        } catch (error) {
          console.error('[Shaka Player] Failed to sync playback rate on fullscreen change:', error)
        }
      }

      // 添加全屏状态变化监听（兼容各浏览器）
      addEventListener(document, 'fullscreenchange', handleFullscreenChange)
      addEventListener(document, 'webkitfullscreenchange', handleFullscreenChange)
      addEventListener(document, 'mozfullscreenchange', handleFullscreenChange)
      addEventListener(document, 'MSFullscreenChange', handleFullscreenChange)

      // 针对 iOS Safari 原生全屏/画中画切换的专门监听
      if (isSafari()) {
        const resetSubtitles = () => {
          console.log(
            '[Shaka Player] Safari presentation/fullscreen state changed, resetting subtitles'
          )
          // iOS Safari 的原生播放器在进出全屏时会篡改 track.mode，需要强制重置
          // 设置较长和多重的延迟，以应对系统动画和播放器内部状态的异步改变
          setTimeout(() => setSubtitleVisibility(currentSubtitle.value !== 'off'), 100)
          setTimeout(() => setSubtitleVisibility(currentSubtitle.value !== 'off'), 500)
        }

        addEventListener(videoPlayer.value, 'webkitpresentationmodechanged', resetSubtitles)
        addEventListener(videoPlayer.value, 'webkitbeginfullscreen', resetSubtitles)
        addEventListener(videoPlayer.value, 'webkitendfullscreen', resetSubtitles)
      }

      // 初始化 Youbora 分析（在所有配置完成后）
      try {
        let youbora_data = { player, ...props.youboraOptions }
        youbora_data.articleId = String(youbora_data?.articleId || '')
        youbora_data.contentId = String(youbora_data?.contentId || '')
        youboras(youbora_data)
      } catch (youboraError) {
        console.warn('Youbora initialization failed:', youboraError)
        // Youbora 失败不应影响播放
      }
    } catch (error) {
      console.error('Shaka Player Init Error:', error)
      // 详细记录错误信息以便调试
      if (error.code === 1002) {
        console.error('HTTP_ERROR: 请检查视频源地址和 CORS 配置')
        console.error('视频源:', props.src)
      }
      emits('stateChange', { state: 'error' })
      if (player) {
        await player.destroy()
        player = null
      }
    } finally {
      initPromise = null
    }
  })()

  return initPromise
}

const play = async () => {
  // 确保播放器已初始化
  if (!player) {
    await initPlayer()
  }

  // 等待播放器完全就绪
  let waitCount = 0
  const maxWait = 50 // 最多等待 5 秒 (50 * 100ms)
  while ((!player || !videoPlayer.value) && waitCount < maxWait) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    waitCount++
  }

  if (player && videoPlayer.value) {
    try {
      // 为了符合浏览器自动播放策略，确保视频是静音的
      // 大多数浏览器允许静音视频自动播放
      if (currentMuted.value && videoPlayer.value.muted === false) {
        videoPlayer.value.muted = true
      }

      // 确保视频元数据已加载
      if (videoPlayer.value.readyState < 1) {
        await new Promise((resolve) => {
          const onLoadedMetadata = () => {
            videoPlayer.value.removeEventListener('loadedmetadata', onLoadedMetadata)
            resolve()
          }
          videoPlayer.value.addEventListener('loadedmetadata', onLoadedMetadata)
          // 超时处理
          setTimeout(resolve, 1000)
        })
      }

      await videoPlayer.value.play()
    } catch (error) {
      console.error('Playback failed:', error)
      // 自动播放被阻止时，发送状态变更通知
      if (error.name === 'NotAllowedError') {
        console.warn('Auto-play prevented by browser policy')
        emits('stateChange', { state: 'autoplay-blocked' })
      } else if (error.name === 'AbortError') {
        // play() 被 pause() 中断是正常行为（如广告播放），不视为错误
        console.warn('Play request interrupted by pause')
      } else {
        emits('stateChange', { state: 'error' })
      }
    }
  }
}

const pause = () => {
  videoPlayer?.value?.pause()
}

// ========== Instream 广告管理（基于 Shaka 内置 AdManager API） ==========

// 广告控制栏显隐状态
const showControls = ref(true)

// 使用普通变量，避免 Vue 响应式代理干扰 IMA SDK 跨域 iframe
let shakaAdManager = null
let imaAdsManager = null // IMA SDK 原生 AdsManager，通过 IMA_AD_MANAGER_LOADED 事件获取
let imaAdsRequest = null
let isAdPlaying = false
let isContentFinished = false
let adBlockedByWhitelist = false // 白名单校验未通过时置为 true，阻止广告呈现
let isAdStoppingByWhitelist = false // 白名单拦截正在停止广告，临时屏蔽 error 事件
let currentAdType = 'pr' // 当前广告类型：'pr' (pre-roll) 或 'mr' (mid-roll)，用于日志区分
const whitelistCache = new Map()

// ============ mr 广告专用 IMA SDK 实例（绕过 Shaka 封装）============
// 原因：Shaka 的 requestClientSideAds 内部强制调用 adsLoader_.contentComplete()，
// 导致第二次请求时 SDK 丢失内容进度上下文，把 mr 当作 pre-roll 立即播放。
// mr 需要独立的 AdsLoader + setAutoPlayAdBreaks(false) + AD_BREAK_READY 手动控制。
// mr 逻辑暂时禁用，解除禁用：移除 requestAds 方法开头的 return 即可
let mrAdDisplayContainer = null // mr 专用 IMA AdDisplayContainer
let mrAdsLoader = null // mr 专用 IMA AdsLoader
let mrAdsManager = null // mr 专用 IMA AdsManager
let mrAdTagUrlCache = '' // 缓存 mr 的 adTagUrl
let isMrAdsManagerInitialized = false // mr AdsManager 是否已 init

// 广告倒计时相关
const adRemainingTime = ref(0) // 广告剩余时间（秒）
const adDuration = ref(0) // 广告总时长（秒）
let adCountdownTimer = null // 轮询定时器 ID

/** 白名单校验：复用 InstreamAds 组件的校验逻辑 */
const checkWhitelist = async (creativeId, mediaFileUrl) => {
  const adType = currentAdType // 'pr' 或 'mr'，用于日志区分
  // TVB 内部广告直接放行
  if (isTvbInHouseAd(mediaFileUrl)) {
    console.log(
      `[Shaka Player] ${adType} 白名单校验 - TVB 内部广告，直接放行, mediaFileUrl:`,
      mediaFileUrl
    )
    return true
  }
  // 缓存命中直接返回
  if (whitelistCache.has(creativeId)) {
    const cached = whitelistCache.get(creativeId)
    console.log(
      `[Shaka Player] ${adType} 白名单校验 - 缓存命中, creativeId:`,
      creativeId,
      'is_whitelist:',
      cached
    )
    return cached
  }
  try {
    const { data } = await checkAdWhitelist(creativeId)
    const isAllowed = data?.is_whitelist ?? true
    console.log(
      `[Shaka Player] ${adType} 白名单校验 - 后台返回, creativeId:`,
      creativeId,
      'response:',
      data,
      'is_whitelist:',
      isAllowed
    )
    whitelistCache.set(creativeId, isAllowed)
    return isAllowed
  } catch (err) {
    console.warn(
      `[Shaka Player] ${adType} 白名单 API 请求失败，容错放行, creativeId:`,
      creativeId,
      'error:',
      err
    )
    return true
  }
}

/**
 * 初始化 Shaka 内置广告管理（替代 TvbInstreamAds 组件 + DOM 手动插入）
 * Shaka Player 5.x 中，UI Controls 创建时已自动调用 adManager.setContainers()
 * 只需创建 IMA AdsRequest 并调用 requestClientSideAds() 即可自动初始化
 */
const initAds = () => {
  if (!props.adTagUrl || !props.showInstreamAds) return
  if (!player || !ui) return

  const ima = window.google?.ima
  if (!ima) {
    console.warn('[Shaka Player] Google IMA SDK 未加载')
    return
  }

  console.log('[Shaka Player] pr initAds 开始, adTagUrl:', props.adTagUrl)
  currentAdType = 'pr' // 初始化 pr 广告
  shakaAdManager = player.getAdManager()

  // 创建 IMA AdsRequest
  imaAdsRequest = new ima.AdsRequest()
  imaAdsRequest.adTagUrl = props.adTagUrl
  imaAdsRequest.requestClientSideAds = true
  console.log('[Shaka Player] pr AdsRequest 创建完成，adTagUrl:', imaAdsRequest.adTagUrl)

  // 监听 IMA AdsManager 加载完成，保存原生引用供白名单拦截时调用 stop()
  shakaAdManager.addEventListener(shaka.ads.Utils.IMA_AD_MANAGER_LOADED, (e) => {
    imaAdsManager = e?.imaAdManager || null
    // 诊断：输出 VMAP 解析出的 ad break 时间点（cue points）
    // start=0 表示 pre-roll，-1 表示 post-roll，正数表示 mid-roll 时间点（秒）
    try {
      const cuePoints = shakaAdManager.getCuePoints()
      console.log(
        `[Shaka Player] ${currentAdType} IMA_AD_MANAGER_LOADED - cuePoints:`,
        JSON.stringify(cuePoints),
        '当前内容 currentTime:',
        videoPlayer.value?.currentTime
      )
    } catch (err) {
      console.warn(
        `[Shaka Player] ${currentAdType} IMA_AD_MANAGER_LOADED - 获取 cuePoints 失败`,
        err
      )
    }
  })

  // 监听 Shaka 广告生命周期事件（Shaka 5.x 使用 shaka.ads.Utils 常量）
  shakaAdManager.addEventListener(shaka.ads.Utils.AD_LOADED, onAdLoaded)
  shakaAdManager.addEventListener(shaka.ads.Utils.AD_STARTED, onAdStarted)
  shakaAdManager.addEventListener(shaka.ads.Utils.AD_STOPPED, onAdStopped)
  shakaAdManager.addEventListener(shaka.ads.Utils.AD_ERROR, onAdErrorEvent)
  // 监听所有广告播放完毕（整个广告 playlist 结束）
  shakaAdManager.addEventListener(shaka.ads.Utils.ALL_ADS_COMPLETED, onAllAdsCompleted)
  // 诊断：监听 SDK 请求暂停内容（要播放广告）— 确认 SDK 何时触发广告播放
  shakaAdManager.addEventListener(shaka.ads.Utils.AD_CONTENT_PAUSE_REQUESTED, () => {
    console.log(
      `[Shaka Player] ${currentAdType} AD_CONTENT_PAUSE_REQUESTED - SDK 请求暂停内容播放广告, currentTime:`,
      videoPlayer.value?.currentTime
    )
  })

  // PC 端自动请求广告；移动端需等用户交互后由 startAds 触发
  if (!isMobile.value) {
    console.log('[Shaka Player] pr PC 端自动请求广告 (requestClientSideAds)')
    shakaAdManager.requestClientSideAds(imaAdsRequest)
  } else {
    console.log('[Shaka Player] pr 移动端等待用户交互后请求广告')
  }
}

/** 广告数据加载完成：白名单校验（在广告呈现之前拦截） */
const onAdLoaded = async (e) => {
  const imaAdEvent = e?.originalEvent
  const ad = imaAdEvent?.getAd?.()
  if (!ad) return

  if (currentAdType === 'mr') {
    console.log('[Shaka Player] mr onAdLoaded - 广告数据加载完成，开始白名单校验')
  } else {
    console.log('[Shaka Player] pr onAdLoaded - 广告数据加载完成，开始白名单校验')
  }

  adBlockedByWhitelist = false
  try {
    const creativeId = ad.getCreativeId?.() || ''
    const mediaFiles = ad.getMediaFiles?.() || []
    const mediaFileUrl = mediaFiles?.[0]?.getUrl?.() || ''
    console.log(
      '[Shaka Player] onAdLoaded 白名单校验, creativeId:',
      creativeId,
      'mediaFileUrl:',
      mediaFileUrl
    )
    const isAllowed = await checkWhitelist(creativeId, mediaFileUrl)
    if (!isAllowed) {
      const adSkippableState = !!imaAdsManager?.getAdSkippableState?.()
      console.warn(`[Shaka Player] 广告 ${creativeId} 未通过白名单校验，阻止呈现`, {
        imaAdsManager,
        ad,
        adSkippableState
      })
      adBlockedByWhitelist = true
      isAdStoppingByWhitelist = true // 设置标志位，屏蔽 stop 触发的 error 事件
      // 直接调用 IMA SDK 原生 skip() 或 stop() 方法（Shaka AdManager 不暴露 skip 或 stop)
      if (adSkippableState) imaAdsManager?.skip()
      else imaAdsManager?.stop()
    } else {
      console.log(`[Shaka Player] 广告 ${creativeId} 通过白名单校验，允许呈现`)
    }
  } catch (err) {
    console.warn('[Shaka Player] 白名单校验异常，容错放行', err, shakaAdManager)
  }
}

/** 广告开始播放：暂停内容 + 隐藏控制栏 + 启动倒计时轮询 */
const onAdStarted = (e) => {
  console.log('[Shaka Player] onAdStarted', e)
  if (currentAdType === 'mr') {
    console.log(
      '[Shaka Player] mr onAdStarted - mid-roll 广告开始播放（SDK 已到达 mid-roll 时间点）'
    )
  } else {
    console.log('[Shaka Player] pr onAdStarted - pre-roll 广告开始播放')
  }

  // 白名单校验已在 onAdLoaded 中完成，未通过则跳过
  if (adBlockedByWhitelist) {
    adBlockedByWhitelist = false
    return
  }

  isAdPlaying = true
  if (videoPlayer.value) videoPlayer.value.pause()
  showControls.value = false
  emits('ad-start')

  // 启动广告倒计时轮询（每 250ms 更新一次）
  const ad = e?.ad
  if (ad) {
    adDuration.value = Math.ceil(ad.getDuration())
    adRemainingTime.value = Math.ceil(ad.getRemainingTime())
    adCountdownTimer = setInterval(() => {
      const remaining = ad.getRemainingTime()
      adRemainingTime.value = remaining > 0 ? Math.ceil(remaining) : 0
    }, 250)
  }
}

/** 广告播放结束：恢复控制栏 + 恢复视频播放 + 清理倒计时 */
const onAdStopped = () => {
  console.log('[Shaka Player] onAdStopped')
  if (currentAdType === 'mr') {
    console.log('[Shaka Player] mr onAdStopped - mid-roll 广告播放结束，恢复内容播放')
  } else {
    console.log('[Shaka Player] pr onAdStopped - pre-roll 广告播放结束，恢复内容播放')
  }
  isAdPlaying = false
  showControls.value = true
  if (videoPlayer.value) {
    videoPlayer.value
      .play()
      .catch(() => {})
      .finally(() => {
        isAdStoppingByWhitelist = false // play 完成后重置标志位
      })
  } else {
    isAdStoppingByWhitelist = false
  }
  emits('ad-end')
  emits('content-resume')

  // 清理广告倒计时
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  adRemainingTime.value = 0
  adDuration.value = 0
}

/** 广告加载失败：恢复控制栏并通知父组件 + 清理倒计时 */
const onAdErrorEvent = (error) => {
  console.warn('[Shaka Player] onAdErrorEvent', error)
  if (currentAdType === 'mr') {
    console.warn('[Shaka Player] mr onAdErrorEvent - mid-roll 广告加载/播放失败')
  } else {
    console.warn('[Shaka Player] pr onAdErrorEvent - pre-roll 广告加载/播放失败')
  }
  isAdPlaying = false
  showControls.value = true
  if (videoPlayer.value) {
    videoPlayer.value
      .play()
      .catch(() => {})
      .finally(() => {
        isAdStoppingByWhitelist = false // play 完成后重置标志位
      })
  } else {
    isAdStoppingByWhitelist = false
  }
  emits('ad-error', error)
  emits('content-resume')

  // 清理广告倒计时
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  adRemainingTime.value = 0
  adDuration.value = 0
}

/** 所有广告播放完毕：整个广告 playlist 结束（含 pr + mid-roll + post-roll） */
const onAllAdsCompleted = () => {
  console.log('[Shaka Player] ALL_ADS_COMPLETED - 所有广告播放完毕')
  isAdPlaying = false
  showControls.value = true
}

// ============ mr 广告专用事件处理函数（绕过 Shaka 封装）============
// 以下函数用于独立的 mr IMA AdsManager，与 pr 的 Shaka 封装互不干扰
// mr 逻辑暂时禁用（函数定义保留但不会被调用），解除禁用：移除 requestAds 方法开头的 return 即可

/**
 * mr 广告断点就绪：SDK 到达 mid-roll 时间点，手动启动广告播放
 * 关键：setAutoPlayAdBreaks(false) 模式下必须在此调用 start()
 */
const onMrAdBreakReady = () => {
  console.log('[Shaka Player] mr AD_BREAK_READY - 到达 mid-roll 时间点，启动广告播放')
  if (mrAdsManager) {
    mrAdsManager.start()
  }
}

/** mr CONTENT_PAUSE_REQUESTED：SDK 请求暂停内容播放广告 */
const onMrContentPauseRequested = () => {
  console.log(
    '[Shaka Player] mr CONTENT_PAUSE_REQUESTED - 暂停内容, currentTime:',
    videoPlayer.value?.currentTime
  )
  isAdPlaying = true
  if (videoPlayer.value) videoPlayer.value.pause()
  showControls.value = false
  emits('ad-start')
}

/** mr CONTENT_RESUME_REQUESTED：SDK 请求恢复内容播放 */
const onMrContentResumeRequested = () => {
  console.log('[Shaka Player] mr CONTENT_RESUME_REQUESTED - 恢复内容播放')
  isAdPlaying = false
  showControls.value = true
  if (videoPlayer.value) videoPlayer.value.play().catch(() => {})
  emits('ad-end')
  emits('content-resume')
  // 清理广告倒计时
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  adRemainingTime.value = 0
  adDuration.value = 0
}

/** mr 广告加载完成：白名单校验（复用现有 checkWhitelist 逻辑） */
const onMrAdLoaded = async (adEvent) => {
  const ad = adEvent?.getAd?.()
  if (!ad) return

  console.log('[Shaka Player] mr onAdLoaded - 广告数据加载完成，开始白名单校验')
  adBlockedByWhitelist = false
  try {
    const creativeId = ad.getCreativeId?.() || ''
    const mediaFiles = ad.getMediaFiles?.() || []
    const mediaFileUrl = mediaFiles?.[0]?.getUrl?.() || ''
    console.log(
      '[Shaka Player] mr onAdLoaded 白名单校验, creativeId:',
      creativeId,
      'mediaFileUrl:',
      mediaFileUrl
    )
    const isAllowed = await checkWhitelist(creativeId, mediaFileUrl)
    if (!isAllowed) {
      console.warn(`[Shaka Player] mr 广告 ${creativeId} 未通过白名单校验，停止广告`)
      adBlockedByWhitelist = true
      isAdStoppingByWhitelist = true
      mrAdsManager?.stop()
    } else {
      console.log(`[Shaka Player] mr 广告 ${creativeId} 通过白名单校验，允许呈现`)
    }
  } catch (err) {
    console.warn('[Shaka Player] mr 白名单校验异常，容错放行', err)
  }
}

/** mr 广告开始播放：启动倒计时 */
const onMrAdStarted = (adEvent) => {
  console.log('[Shaka Player] mr onAdStarted - mid-roll 广告开始播放')
  if (adBlockedByWhitelist) {
    adBlockedByWhitelist = false
    return
  }
  isAdPlaying = true
  // 启动广告倒计时轮询
  const ad = adEvent?.getAd?.()
  if (ad) {
    adDuration.value = Math.ceil(ad.getDuration())
    adRemainingTime.value = Math.ceil(ad.getRemainingTime())
    adCountdownTimer = setInterval(() => {
      const remaining = ad.getRemainingTime()
      adRemainingTime.value = remaining > 0 ? Math.ceil(remaining) : 0
    }, 250)
  }
}

/** mr 单条广告播放完成 */
const onMrAdComplete = () => {
  console.log('[Shaka Player] mr onAdComplete - 单条 mid-roll 广告播放完成')
  // 清理倒计时
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  adRemainingTime.value = 0
  adDuration.value = 0
}

/** mr 所有广告播放完毕 */
const onMrAllAdsCompleted = () => {
  console.log('[Shaka Player] mr ALL_ADS_COMPLETED - 所有 mid-roll 广告播放完毕')
  isAdPlaying = false
  showControls.value = true
  isAdStoppingByWhitelist = false
}

/** mr 广告错误 */
const onMrAdError = (adErrorEvent) => {
  const error = adErrorEvent?.getError ? adErrorEvent.getError() : adErrorEvent
  console.warn('[Shaka Player] mr onAdError - mid-roll 广告错误', error)
  if (isAdStoppingByWhitelist) {
    // 白名单拦截触发的错误，忽略
    return
  }
  isAdPlaying = false
  showControls.value = true
  if (videoPlayer.value) {
    videoPlayer.value
      .play()
      .catch(() => {})
      .finally(() => {
        isAdStoppingByWhitelist = false
      })
  } else {
    isAdStoppingByWhitelist = false
  }
  emits('ad-error', error)
  emits('content-resume')
  // 清理倒计时
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  adRemainingTime.value = 0
  adDuration.value = 0
}

/**
 * mr AdsManager 加载完成回调
 * 创建 AdsManager 并注册事件监听，等待 AD_BREAK_READY 在 mid-roll 时间点播放
 */
const onMrAdsManagerLoaded = (adsManagerLoadedEvent) => {
  const ima = window.google?.ima
  if (!ima) return

  console.log('[Shaka Player] mr onMrAdsManagerLoaded - VMAP 解析完成')

  // 创建 AdsManager，传入 video 元素用于内容进度跟踪
  const adsRenderingSettings = new ima.AdsRenderingSettings()
  adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true
  mrAdsManager = adsManagerLoadedEvent.getAdsManager(videoPlayer.value, adsRenderingSettings)

  // 输出 VMAP 解析的 cue points（诊断 mid-roll 时间点）
  try {
    const cuePoints = mrAdsManager.getCuePoints()
    console.log('[Shaka Player] mr cuePoints:', JSON.stringify(cuePoints))
  } catch (e) {
    console.warn('[Shaka Player] mr 获取 cuePoints 失败', e)
  }

  // 注册 IMA SDK 事件监听
  mrAdsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onMrAdError)
  mrAdsManager.addEventListener(ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, onMrContentPauseRequested)
  mrAdsManager.addEventListener(
    ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
    onMrContentResumeRequested
  )
  mrAdsManager.addEventListener(ima.AdEvent.Type.LOADED, onMrAdLoaded)
  mrAdsManager.addEventListener(ima.AdEvent.Type.STARTED, onMrAdStarted)
  mrAdsManager.addEventListener(ima.AdEvent.Type.COMPLETE, onMrAdComplete)
  mrAdsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, onMrAllAdsCompleted)
  // 关键：监听 AD_BREAK_READY，在 mid-roll 时间点手动 start()
  mrAdsManager.addEventListener(ima.AdEvent.Type.AD_BREAK_READY, onMrAdBreakReady)

  // 初始化 AdsManager（内容进度跟踪从此开始）
  try {
    mrAdsManager.init(
      videoPlayer.value.offsetWidth,
      videoPlayer.value.offsetHeight,
      ima.ViewMode.NORMAL
    )
    isMrAdsManagerInitialized = true
    console.log(
      '[Shaka Player] mr AdsManager init 完成，等待 SDK 在 mid-roll 时间点触发 AD_BREAK_READY'
    )
    // 注意：不调用 start()！setAutoPlayAdBreaks(false) 模式下，
    // SDK 会在 mid-roll 时间点触发 AD_BREAK_READY，在回调中调用 start()
  } catch (e) {
    console.error('[Shaka Player] mr AdsManager init 失败', e)
    onMrAdError(e)
  }
}

/**
 * 初始化 mr 专用 IMA AdsLoader（绕过 Shaka 封装）
 * 关键：调用 setAutoPlayAdBreaks(false) 禁用自动播放，
 *       通过 AD_BREAK_READY 事件在 mid-roll 时间点手动 start()
 */
const initMrAdsLoader = () => {
  if (mrAdsLoader) return // 已初始化

  const ima = window.google?.ima
  if (!ima) {
    console.warn('[Shaka Player] mr initMrAdsLoader - IMA SDK 未加载')
    return
  }

  // 复用 Shaka 的客户端广告容器 DOM（避免 UI 定位问题）
  let adContainer
  try {
    adContainer = ui?.getControls?.()?.getClientSideAdContainer?.()
  } catch (e) {
    console.warn('[Shaka Player] mr 获取 Shaka 广告容器失败', e)
  }
  if (!adContainer) {
    console.warn('[Shaka Player] mr 未获取到 Shaka 广告容器，mr 无法播放')
    return
  }

  console.log('[Shaka Player] mr initMrAdsLoader - 创建独立 IMA AdsLoader')
  // 创建独立的 AdDisplayContainer（复用 Shaka 的容器 DOM 和 video 元素）
  mrAdDisplayContainer = new ima.AdDisplayContainer(adContainer, videoPlayer.value)
  mrAdDisplayContainer.initialize()

  // 创建独立的 AdsLoader
  mrAdsLoader = new ima.AdsLoader(mrAdDisplayContainer)
  // 关键：禁用 ad break 自动播放，改为 AD_BREAK_READY 手动触发
  mrAdsLoader.getSettings().setAutoPlayAdBreaks(false)
  mrAdsLoader.getSettings().setPlayerType('shaka-player-mr')
  mrAdsLoader.getSettings().setPlayerVersion(shaka.Player.version)

  // 监听 ADS_MANAGER_LOADED
  mrAdsLoader.addEventListener(
    ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
    onMrAdsManagerLoaded
  )
  // 监听 AD_ERROR
  mrAdsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onMrAdError)
}

/** 视频内容结束 - 通知 SDK 触发 Post-roll 广告 */
const onContentEnded = () => {
  console.log('[Shaka Player] onContentEnded, isContentFinished:', isContentFinished)
  if (isContentFinished) return
  isContentFinished = true
  // 通知 pr 的 Shaka AdManager 内容已结束，触发 post-roll 广告
  if (shakaAdManager) {
    shakaAdManager.contentComplete()
  }
  // 通知 mr 的独立 AdsLoader 内容已结束（触发 mr VMAP 中的 post-roll，如果有）
  // mr 逻辑暂时禁用：mrAdsLoader 始终为 null，此分支不会进入
  if (mrAdsLoader) {
    console.log('[Shaka Player] mr contentComplete - 通知 mr AdsLoader 内容结束')
    mrAdsLoader.contentComplete()
  }
  emits('content-resume')
}

// 切换到上一集视频
const prev = () => {
  emits('prev')
}

// 切换到下一集视频
const next = () => {
  emits('next')
}

// 监听 videoClarity 和 subtitle 变化
watch(
  () => props.videoClarity,
  (newVal) => {
    if (newVal && player && videoContainer.value) {
      // 重新初始化控制按钮
      removeCustomButtons()
      addControlButtons(videoContainer.value)
    }
  },
  { deep: true }
)

watch(
  () => props.subtitle,
  (newVal) => {
    if (newVal && player && videoContainer.value) {
      // 重新初始化控制按钮
      removeCustomButtons()
      addControlButtons(videoContainer.value)
    }
  },
  { deep: true }
)

onMounted(() => {
  initPlayer()
})

onUnmounted(async () => {
  cleanupSubtitleListeners()
  removeAllEventListeners()
  // 清理 Shaka 广告管理器事件监听
  if (shakaAdManager) {
    shakaAdManager.removeEventListener(shaka.ads.Utils.AD_LOADED, onAdLoaded)
    shakaAdManager.removeEventListener(shaka.ads.Utils.AD_STARTED, onAdStarted)
    shakaAdManager.removeEventListener(shaka.ads.Utils.AD_STOPPED, onAdStopped)
    shakaAdManager.removeEventListener(shaka.ads.Utils.AD_ERROR, onAdErrorEvent)
    shakaAdManager.removeEventListener(shaka.ads.Utils.ALL_ADS_COMPLETED, onAllAdsCompleted)
    shakaAdManager = null
    imaAdsManager = null
  }
  // 清理 mr 专用 IMA SDK 实例（绕过 Shaka 封装的独立实例）
  // mr 逻辑暂时禁用：mrAdsManager/mrAdsLoader/mrAdDisplayContainer 始终为 null，以下分支不会进入
  if (mrAdsManager) {
    try {
      mrAdsManager.destroy()
    } catch (e) {
      console.error('[Shaka Player] mr AdsManager destroy 失败', e)
    }
    mrAdsManager = null
  }
  if (mrAdsLoader) {
    try {
      const ima = window.google?.ima
      if (ima) {
        mrAdsLoader.removeEventListener(
          ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          onMrAdsManagerLoaded
        )
        mrAdsLoader.removeEventListener(ima.AdErrorEvent.Type.AD_ERROR, onMrAdError)
      }
    } catch (e) {
      console.error('[Shaka Player] mr AdsLoader removeEventListener 失败', e)
    }
    // 不调用 contentComplete，直接 destroy
    try {
      mrAdsLoader.destroy()
    } catch (e) {
      console.error('[Shaka Player] mr AdsLoader destroy 失败', e)
    }
    mrAdsLoader = null
  }
  if (mrAdDisplayContainer) {
    try {
      mrAdDisplayContainer.destroy()
    } catch (e) {
      console.error('[Shaka Player] mr AdDisplayContainer destroy 失败', e)
    }
    mrAdDisplayContainer = null
  }
  isMrAdsManagerInitialized = false
  mrAdTagUrlCache = ''
  whitelistCache.clear()
  // 清理广告倒计时定时器
  if (adCountdownTimer) {
    clearInterval(adCountdownTimer)
    adCountdownTimer = null
  }
  if (player) {
    await player.destroy()
    player = null
  }
})

defineExpose({
  play,
  pause,
  initPlayer,
  prev,
  next,
  startAds: () => {
    if (shakaAdManager && imaAdsRequest) {
      console.log('[Shaka Player] pr startAds - 移动端用户交互触发，请求 pre-roll 广告')
      shakaAdManager.requestClientSideAds(imaAdsRequest)
    }
  },
  // 请求 mr 广告（绕过 Shaka 封装，使用独立 IMA AdsLoader + setAutoPlayAdBreaks(false)）
  // 原因：Shaka 的 requestClientSideAds 内部强制调用 contentComplete()，
  // 导致 SDK 丢失内容进度上下文，把 mr 当作 pre-roll 立即播放。
  // mr 使用独立 AdsLoader + AD_BREAK_READY 在 mid-roll 时间点手动 start()。
  requestAds: (adTagUrl) => {
    // mr 逻辑暂时禁用：直接 return，不执行任何 mr 逻辑
    // 解除禁用：删除此 return 语句即可恢复 mr 完整逻辑
    if (!adTagUrl) return
    return // ← mr 逻辑暂时禁用
    // const ima = window.google?.ima
    // if (!ima) {
    //   console.warn('[Shaka Player] mr requestAds 跳过: IMA SDK 未加载')
    //   return
    // }
    // console.log('[Shaka Player] mr requestAds 开始, adTagUrl:', adTagUrl)
    // currentAdType = 'mr'
    // console.log(
    //   '[Shaka Player] mr requestAds - 当前内容 currentTime:',
    //   videoPlayer.value?.currentTime,
    //   '是否暂停:',
    //   videoPlayer.value?.paused
    // )

    // // 懒初始化 mr 专用 AdsLoader（首次调用时创建）
    // initMrAdsLoader()
    // if (!mrAdsLoader) {
    //   console.warn('[Shaka Player] mr requestAds - AdsLoader 初始化失败')
    //   return
    // }

    // // 如果 mr AdsManager 已存在（前一会话），先销毁
    // if (mrAdsManager) {
    //   try {
    //     mrAdsManager.destroy()
    //   } catch (e) {
    //     console.warn('[Shaka Player] mr 销毁旧 AdsManager 失败', e)
    //   }
    //   mrAdsManager = null
    //   isMrAdsManagerInitialized = false
    // }

    // // 创建 AdsRequest 并请求 VMAP
    // // 注意：不调用 contentComplete()，保持内容进度连续
    // const adsRequest = new ima.AdsRequest()
    // adsRequest.adTagUrl = adTagUrl
    // adsRequest.requestClientSideAds = true
    // console.log('[Shaka Player] mr AdsRequest 创建完成，请求 VMAP（不调用 contentComplete）')
    // mrAdTagUrlCache = adTagUrl
    // mrAdsLoader.requestAds(adsRequest)
    // console.log('[Shaka Player] mr requestAds 已调用，等待 ADS_MANAGER_LOADED → AD_BREAK_READY')
  }
})
</script>

<template>
  <div
    class="tvb-player-shaka"
    ref="videoContainer"
    :class="{ 'is-mobile-device': isMobile, 'ad-playing': !showControls }"
  >
    <video
      class="tvb-player-shaka-video"
      ref="videoPlayer"
      :id="videoId"
      :muted="currentMuted"
      :poster="props.posterUrl"
      x-webkit-airplay="true"
      webkit-playsinline=""
      playsinline="true"
    ></video>
    <!-- 广告倒计时覆盖层 -->
    <div v-if="!showControls" class="tvb-ad-countdown">
      <span class="tvb-ad-countdown-label">廣告</span>
      <span class="tvb-ad-countdown-time">{{ adRemainingTime }}秒</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-player-shaka {
  height: 100%;
  width: 100%;

  .tvb-player-shaka-video {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  // 广告播放期间禁用 Shaka 控制栏
  &.ad-playing {
    :deep(.shaka-controls-container) {
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
    }
  }

  // 广告倒计时覆盖层
  .tvb-ad-countdown {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 4px;
    color: #fff;
    font-size: 14px;
    pointer-events: none;

    .tvb-ad-countdown-label {
      opacity: 0.8;
    }

    .tvb-ad-countdown-time {
      font-weight: bold;
      font-variant-numeric: tabular-nums;
    }
  }

  // :deep(.shaka-controls-container .shaka-play-button) {
  //   opacity: 1;
  // }
  // :deep(.shaka-controls-container  .shaka-big-buttons-container .shaka-play-button) {
  //   opacity: 0;
  // }

  :deep(.shaka-content-title) {
    display: none !important;
  }

  // :deep(.shaka-spinner-container) {
  //   opacity: 0;
  // }

  :deep(.shaka-controls-container) {
    z-index: 11;
    .tvb-player-shaka-header {
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 40px;
      padding: 2.5%;
      opacity: 0;
      transition: opacity 0.5s ease-in;

      .tvb-player-shaka-header-title {
        color: #fff;
        font-size: 16px;
        text-shadow: 0 0 10px rgba(0, 0, 0, 0.8);
      }
    }

    &[shown='true'] {
      .tvb-player-shaka-header {
        opacity: 1;
      }
    }
  }

  /* 修复 Chrome 浏览器中进度条和音量控制条的黑色边框问题 */
  :deep(.shaka-range-element) {
    /* 移除 Chrome 中 range input 的默认边框和轮廓 */
    outline: none !important;
    border: none !important;

    /* 确保在聚焦状态下也不显示边框 */
    &:focus {
      outline: none !important;
      border: none !important;
      box-shadow: none !important;
    }

    /* 针对 Chrome 和基于 Chromium 的浏览器 */
    &::-webkit-slider-runnable-track {
      border: none !important;
      outline: none !important;
    }

    &::-webkit-slider-thumb {
      border: none !important;
      outline: none !important;
      /* 确保滑块在 Chrome 中正确渲染 */
      -webkit-appearance: none;
      appearance: none;
    }
  }

  /* 修复进度条容器的边框问题 */
  :deep(.shaka-seek-bar-container) {
    outline: none !important;
    border: none !important;

    /* 修复 Chrome 中可能由于 background-clip 导致的视觉问题 */
    background-clip: padding-box !important;
  }

  /* 修复音量控制条容器的边框问题 */
  :deep(.shaka-volume-bar-container) {
    outline: none !important;
    border: none !important;
  }

  /* 修复 range container 的边框问题 */
  :deep(.shaka-range-container) {
    outline: none !important;
    border: none !important;
  }

  /* 自定义上一集/下一集按钮样式优化 */
  :deep(.shaka-custom-button) {
    /* 确保过渡动画在所有浏览器中正常工作 */
    will-change: opacity, transform;
    -webkit-transition:
      opacity 0.3s ease-in-out,
      transform 0.2s ease;
    -moz-transition:
      opacity 0.3s ease-in-out,
      transform 0.2s ease;
    -ms-transition:
      opacity 0.3s ease-in-out,
      transform 0.2s ease;
    transition:
      opacity 0.3s ease-in-out,
      transform 0.2s ease;

    /* 禁用按钮时的样式 */
    &:disabled {
      opacity: 0.3 !important;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* 确保 SVG 图标正确显示 */
    svg {
      width: 24px;
      height: 24px;
      display: block;
    }

    /* 鼠标悬停时的缩放效果 */
    &:hover {
      transform: scale(1.05);
    }

    /* 点击时的缩放效果 */
    &:active {
      transform: scale(0.95);
    }
  }

  /* 上一集按钮特定样式 */
  :deep(.shaka-prev-button) {
    margin-right: 4px;
  }

  /* 下一集按钮特定样式 */
  :deep(.shaka-next-button) {
    margin-left: 4px;
  }

  /* 自定义下拉菜单样式 - Desktop popups with transparent background */
  :deep(.shaka-custom-dropdown) {
    background: rgba(0, 0, 0, 0.85) !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
    animation: fadeInUp 0.2s ease-out !important;

    .shaka-custom-dropdown-item {
      padding: 10px 16px !important;
      transition: background 0.15s ease !important;

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  }

  /* 分辨率、字幕、播放速度按钮样式 */
  :deep(.shaka-clarity-button),
  :deep(.shaka-subtitle-button),
  :deep(.shaka-playback-rate-button) {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.5px;
  }

  /* Mobile "More" Menu Styles (Hierarchical Navigation) */
  :deep(.shaka-more-menu) {
    animation: slideUp 0.2s ease-out;

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes slideDown {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(10px);
      }
    }

    /* Menu content container */
    .more-menu-content {
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.3) transparent;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
      }
    }

    /* Main menu items */
    .more-menu-item {
      &:last-child {
        border-bottom: none;
      }

      &:active {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    /* Submenu header with back button */
    .more-submenu-header {
      &:active {
        background: rgba(255, 255, 255, 0.1);
      }

      .more-submenu-back {
        &:hover {
          opacity: 0.8;
        }

        &:active {
          opacity: 0.6;
        }
      }
    }

    /* Submenu options */
    .more-submenu-option {
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      &:last-child {
        border-bottom: none;
      }

      &:active {
        background: rgba(255, 255, 255, 0.15);
      }

      &.active {
        color: #00d4ff;
        background: rgba(0, 212, 255, 0.1);
      }
    }
  }

  /* ============================================
     Mobile UI Scale Optimization
     ============================================ */

  /* Small screens (< 375px) - 20% scale reduction */
  @media (max-width: 374px) {
    /* Control bar buttons - smaller size */
    :deep(.shaka-controls-button-panel) {
      padding: 0 4px;

      .shaka-mute-button,
      .shaka-play-button,
      .shaka-fullscreen-button,
      .shaka-button {
        padding: 6px !important;
        min-width: 36px !important;
        min-height: 36px !important;
        width: 36px !important;
        height: 36px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        svg {
          width: 18px !important;
          height: 18px !important;
        }
      }

      /* Fix icon size for Shaka built-in buttons */
      .shaka-mute-button .shaka-ui-icon,
      .shaka-fullscreen-button .shaka-ui-icon {
        font-size: 18px !important;
        line-height: 1 !important;
      }
    }

    /* Custom buttons (prev/next) */
    :deep(.shaka-custom-button) {
      padding: 6px !important;

      svg {
        width: 18px !important;
        height: 18px !important;
      }
    }

    /* More menu - compact size */
    :deep(.shaka-more-menu) {
      min-width: 140px !important;

      .more-menu-item {
        padding: 8px 12px !important;
        font-size: 12px !important;

        .more-menu-item-label {
          font-size: 11px !important;
        }
      }

      .more-submenu-header {
        padding: 8px 12px !important;

        .more-submenu-title {
          font-size: 12px !important;
        }
      }

      .more-submenu-option {
        padding: 8px 12px !important;
        font-size: 12px !important;
      }
    }

    /* Time display */
    :deep(.shaka-current-time) {
      font-size: 11px !important;
    }

    :deep(.shaka-duration) {
      font-size: 11px !important;
    }

    /* Control bar height */
    :deep(.shaka-bottom-controls) {
      padding: 4px 8px !important;
    }
  }

  /* Medium screens (375px - 414px) - 15% scale reduction */
  @media (min-width: 375px) and (max-width: 414px) {
    /* Control bar buttons */
    :deep(.shaka-controls-button-panel) {
      padding: 0 6px;

      .shaka-button {
        padding: 7px !important;
        min-width: 38px !important;
        min-height: 38px !important;

        svg {
          width: 20px !important;
          height: 20px !important;
        }
      }

      /* Fix Shaka built-in mute/fullscreen buttons on mobile */
      .shaka-mute-button,
      .shaka-fullscreen-button {
        width: 38px !important;
        height: 38px !important;
        min-width: 38px !important;
        min-height: 38px !important;
        padding: 7px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        .shaka-ui-icon {
          font-size: 20px !important;
          line-height: 1 !important;
        }
      }
    }

    /* Custom buttons */
    :deep(.shaka-custom-button) {
      padding: 7px !important;

      svg {
        width: 20px !important;
        height: 20px !important;
      }
    }

    /* More menu */
    :deep(.shaka-more-menu) {
      min-width: 150px !important;

      .more-menu-item {
        padding: 9px 14px !important;
        font-size: 12px !important;

        .more-menu-item-label {
          font-size: 11px !important;
        }
      }

      .more-submenu-header {
        padding: 9px 14px !important;

        .more-submenu-title {
          font-size: 12px !important;
        }
      }

      .more-submenu-option {
        padding: 9px 14px !important;
        font-size: 12px !important;
      }
    }

    /* Time display */
    :deep(.shaka-current-time) {
      font-size: 12px !important;
    }

    :deep(.shaka-duration) {
      font-size: 12px !important;
    }

    /* Control bar height */
    :deep(.shaka-bottom-controls) {
      padding: 5px 10px !important;
    }
  }

  /* Large mobile screens (> 414px) - 10% scale reduction */
  @media (min-width: 415px) and (max-width: 767px) {
    /* Control bar buttons */
    :deep(.shaka-controls-button-panel) {
      padding: 0 8px;

      .shaka-button {
        padding: 8px !important;
        min-width: 40px !important;
        min-height: 40px !important;

        svg {
          width: 22px !important;
          height: 22px !important;
        }
      }

      /* Fix Shaka built-in mute/fullscreen buttons on mobile */
      .shaka-mute-button,
      .shaka-fullscreen-button {
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        min-height: 40px !important;
        padding: 8px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        .shaka-ui-icon {
          font-size: 22px !important;
          line-height: 1 !important;
        }
      }
    }

    /* Custom buttons */
    :deep(.shaka-custom-button) {
      padding: 8px !important;

      svg {
        width: 22px !important;
        height: 22px !important;
      }
    }

    /* More menu */
    :deep(.shaka-more-menu) {
      min-width: 160px !important;

      .more-menu-item {
        padding: 10px 16px !important;
        font-size: 13px !important;

        .more-menu-item-label {
          font-size: 12px !important;
        }
      }

      .more-submenu-header {
        padding: 10px 16px !important;

        .more-submenu-title {
          font-size: 13px !important;
        }
      }

      .more-submenu-option {
        padding: 10px 16px !important;
        font-size: 13px !important;
      }
    }

    /* Time display */
    :deep(.shaka-current-time) {
      font-size: 12px !important;
    }

    :deep(.shaka-duration) {
      font-size: 12px !important;
    }

    /* Control bar height */
    :deep(.shaka-bottom-controls) {
      padding: 6px 12px !important;
    }
  }

  /* Hide desktop buttons on mobile */
  &.is-mobile-device {
    :deep(.shaka-clarity-button),
    :deep(.shaka-subtitle-button),
    :deep(.shaka-playback-rate-button) {
      display: none !important;
    }

    /* Fix mute & fullscreen button icon centering on mobile (UA-based, viewport-independent)
       Covers both normal and Android fullscreen (landscape viewport > 767px bypasses media queries) */
    :deep(.shaka-mute-button),
    :deep(.shaka-fullscreen-button) {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 8px !important;

      .shaka-ui-icon {
        font-size: 22px !important;
        line-height: 1 !important;
      }
    }

    /* Fullscreen state: Android Chrome uses :-webkit-full-screen on the video container */
    :deep(.shaka-video-container:fullscreen),
    :deep(.shaka-video-container:-webkit-full-screen) {
      .shaka-mute-button,
      .shaka-fullscreen-button {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 48px !important;
        height: 48px !important;
        padding: 8px !important;

        .shaka-ui-icon {
          font-size: 24px !important;
          line-height: 1 !important;
        }
      }
    }

    /* 将移动端整个字幕容器的底部抬高，避开播放控制栏 */
    :deep(.shaka-text-container) {
      bottom: 5% !important;
    }

    /* 强制覆盖移动端 Shaka UI 字幕的内联位置，将字幕盒固定在底部正中央，无视右键菜单的方位配置 */
    :deep(.shaka-text-region) {
      top: auto !important;
      bottom: 0 !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      width: 100% !important;
      text-align: center !important;
      align-items: center !important;
    }
  }

  /* Desktop adaptation */
  &:not(.is-mobile-device) {
    :deep(.shaka-more-button) {
      display: none !important;
    }
  }

  /* ============================================
     Safari 字幕样式优化
     ============================================ */

  /* 确保 Shaka Player 字幕容器正确显示 */
  :deep(.shaka-text-container) {
    color: #fff;

    .shaka-text-region {
      span {
        font-weight: normal !important;
      }

      /* 字幕样式优化 - 支持 JS 动态注入的 --subtitle-font-size */
      > span > span > span {
        background-color: rgba(0, 0, 0, 0.8) !important;
        padding: 3px 12px !important;
        border-radius: 6px !important;
        color: #fff !important;
        font-weight: normal !important;
        font-size: var(--subtitle-font-size, 24px) !important;

        span {
          font-weight: normal !important;
          font-family: inherit;
        }
      }
    }
  }

  /* 原生字幕轨道样式（Safari 降级方案使用） */
  ::cue {
    background-color: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: 6px 12px;
    border-radius: 6px;
  }

  /* PC 端原生字幕保留绝对字体大小配置，移动端交由系统自适应 */
  &:not(.is-mobile-device) {
    ::cue {
      font-size: var(--subtitle-font-size, 24px);
    }
  }
}
</style>
